<template>
  <div class="timing-container">
    <!-- Toast per messaggi -->
    <Toast />

    <!-- Header con filtri dipendenti -->
    <div class="timing-header mb-4">
      <div class="flex gap-3 items-center flex-wrap">
        <MultiSelect
          v-model="dipendentiSelezionati"
          :options="dipendentiFormattati"
          optionLabel="cognome_nome"
          optionValue="id"
          :placeholder="$root.trans.seleziona_dipendenti"
          :disabled="loading"
        />
        <Button
          :label="$root.trans.cerca"
          @click="cerca"
          class="p-button-sm"
          icon="pi pi-search"
        />
      </div>
    </div>

    <!-- Header con navigazione date -->
    <div class="timing-header mb-4">
      <div class="flex gap-3 items-center flex-wrap justify-center">
        <!-- Select visualizzazione giorni -->
        <div class="visualizzazione-selector">
          <Dropdown
            v-model="timingSceltoInterno"
            :options="opzioniVisualizzazione"
            optionLabel="label"
            optionValue="value"
            :placeholder="$root.trans.visualizzazione"
            @change="cambiaVisualizzazione"
            :disabled="loading"
            class="p-dropdown-sm"
          />
        </div>

        <!-- Pulsante Oggi -->
        <Button
          :label="$root.trans.today"
          @click="vaiAOggi"
          :disabled="loading || isGiornoCorrente()"
          class="p-button-sm"
          :class="isGiornoCorrente() ? 'p-button-success' : 'p-button-secondary'"
          icon="pi pi-calendar"
        />

        <!-- Navigatore date con frecce -->
        <div class="date-navigator flex items-center gap-2">
          <Button
            icon="pi pi-chevron-left"
            @click="navigaIndietro"
            :disabled="loading"
            class="p-button-sm p-button-outlined"
          />

          <!-- Display del mese/anno corrente -->
          <div class="periodo-display">
            <span class="mese-anno">{{ formatMeseAnno() }}</span>
          </div>

          <Button
            icon="pi pi-chevron-right"
            @click="navigaAvanti"
            :disabled="loading"
            class="p-button-sm p-button-outlined"
          />
        </div>

        <!-- Selettore data centrale con visualizzazione intervallo -->
        <div class="date-picker-container">
          <!-- Display dell'intervallo cliccabile -->
          <div
            class="interval-display"
            @click="toggleDatePicker"
            :class="{ 'active': showDatePicker }"
          >
            <span class="interval-text">{{ formatIntervalloDisplay() }}</span>
            <i class="pi pi-calendar calendar-icon"></i>
          </div>

          <!-- DatePicker che appare quando necessario -->
          <div v-if="showDatePicker" class="date-picker-wrapper">
            <DatePicker
              ref="singleDatePicker"
              v-model="dataCentrale"
              dateFormat="dd/mm/yy"
              :disabled="loading"
              @date-select="onDateSelect"
              class="date-picker-overlay"
              showIcon
              :inline="true"
            />
          </div>
        </div>

        <div v-if="loading" class="flex items-center gap-2">
          <ProgressSpinner style="width: 20px; height: 20px" strokeWidth="4"/>
          <small>{{ $root.trans.caricamento }}</small>
        </div>
      </div>
    </div>

    <!-- Tabella principale -->
    <div class="timing-table-container">
      <div class="table-responsive">
        <table class="timing-table">
          <!-- Header con le date -->
          <thead>
          <tr>
            <th class="dipendente-col sticky-col">{{ $root.trans.dipendente }}</th>
            <th
              v-for="dataObj in intestazioniInterne"
              :key="dataObj.giorno"
              class="data-col"
              :style="{ color: dataObj.colore }"
            >
              <div class="header-date">
                <div class="day-name">{{ formatDayName(dataObj.giorno) }}</div>
                <div class="day-number">{{ formatDay(dataObj.giorno) }}</div>
              </div>
            </th>
            <th class="totale-col">{{ $root.trans.totale }}</th>
          </tr>
          </thead>

          <!-- Body con i dati -->
          <tbody>
          <tr
            v-for="(dipendente, dipendente_id) in datigrigliaInterna"
            :key="dipendente_id"
            class="dipendente-row"
          >
            <!-- Nome dipendente -->
            <td class="dipendente-name sticky-col">
              {{ dipendente.cognome_nome }}
            </td>

            <!-- Celle per ogni giorno -->
            <td
              v-for="dataObj in intestazioniInterne"
              :key="`${dipendente_id}-${dataObj.giorno}`"
              :class="getCellClass(dataObj.giorno, dipendente[dataObj.giorno], dataObj.colore)"
              class="data-cell"
              @click="apriDettaglio(dipendente_id, dataObj.giorno, dipendente[dataObj.giorno])"
            >
              <div class="cell-content">
                <!-- Ore/Minuti -->
                <div class="ore-value" v-if="dipendente[dataObj.giorno] && dipendente[dataObj.giorno].minuti && dipendente[dataObj.giorno].minuti !== '00:00'">
                  {{ dipendente[dataObj.giorno].minuti }}
                </div>
                <div class="ore-value empty" v-else>
                  0
                </div>

                <!-- Indicatori -->
                <div class="indicators" v-if="dipendente[dataObj.giorno]">
                  <span
                    v-if="dipendente[dataObj.giorno].assenze && dipendente[dataObj.giorno].assenze > 0"
                    class="indicator assenza"
                    :title="$root.trans.assenza"
                  >
                    A
                  </span>
                  <span
                    v-if="dipendente[dataObj.giorno].straordinari && dipendente[dataObj.giorno].straordinari > 0"
                    class="indicator straordinario"
                    :title="$root.trans.straordinario"
                  >
                    S
                  </span>
                  <span
                    v-if="dipendente[dataObj.giorno].avvisi && dipendente[dataObj.giorno].avvisi > 0"
                    class="indicator avviso"
                    :title="$root.trans.avviso"
                  >
                    !
                  </span>
                  <span
                    v-if="dipendente[dataObj.giorno].smartworking && dipendente[dataObj.giorno].smartworking > 0"
                    class="indicator smartworking"
                    :title="$root.trans.smartworking"
                  >
                    SWo
                  </span>
                  <span
                    v-if="dipendente[dataObj.giorno].confermato && dipendente[dataObj.giorno].confermato > 0"
                    class="indicator confermato"
                    :title="$root.trans.confermato"
                  >
                    ✓
                  </span>
                </div>
              </div>
            </td>

            <!-- Totale ore -->
            <td class="totale-cell">
              <strong>{{ dipendente.totale_ore || '0' }}</strong>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Drawer per dettagli -->
    <Drawer
      v-model:visible="drawerVisible"
      :header="$root.trans.dettagli_giornata"
      position="right"
      class="timing-drawer"
    >
      <div v-if="dettaglioSelezionato" class="dettaglio-content">
        {{ dettaglioSelezionato }}
        <div>
          <button v-if="(dettaglioSelezionato.dati.confermato == 0 || dettaglioSelezionato.dati.confermato == null) && dettaglioSelezionato.dati.avvisi == 0" class="p-button p-button-sm p-button-info mt-2" @click="apriConfermaGiornata">{{ $root.trans.conferma }}</button>
          <button v-if="dettaglioSelezionato.dati.confermato == 0 ||  dettaglioSelezionato.dati.confermato == null" class="p-button p-button-sm p-button-info mt-2" @click="apriModificaTimbrature">{{ $root.trans.modifica }}</button>
          <button v-if="dettaglioSelezionato.dati.avvisi == 1 && dettaglioSelezionato.dati.timbrature_cantieri_diversi == 1 " class="p-button p-button-sm p-button-info mt-2" @click="verificaTutteTimbrature(dettaglioSelezionato)">{{ $root.trans.verifica_tutto }}</button>
        </div>
        <h4>DEVI METTERE L'APPROVAZIONE DELLE SINGOLE TIMBRATURE, LA POSSIBILITA' DI INSERIRE L'USCITA,
          PRATICAMENTE TUTTO QUELLO CHE FACEVA VERIFICA LETTURE IN MODO DA GESTIRE IL TUTTO AL MEGLIO.
          INOLTRE CI VUOLE IL CONFERMA TUTTO PER CONFERMARE TUTTE LE TIMBRATURE ED IL "DISCONFERMA" CHE PUO' ESSERE FATTO ENTRO I 3GG DALLA CONFERMA</h4>
        <h4>{{ $root.trans.dettaglio_giornata }}</h4>
        <h5>{{ formatDayName(dettaglioSelezionato.data) }} {{ formatDate(dettaglioSelezionato.data) }}</h5>
        <div class="dettaglio-item">
          <label>{{ dettaglioSelezionato.dipendente }}</label>
        </div>

        <div class="dettaglio-item">
          <label>{{ $root.trans.ore_lavorate }}:</label>
          <span>{{ dettaglioSelezionato.dati?.minuti || '0' }}</span>
        </div>
        <div class="dettaglio-item">
          <label>{{ $root.trans.ore_contratto }}:</label>
          <span>{{ dettaglioSelezionato.dati?.ore_contratto || '0' }}</span>
        </div>

        <div class="dettaglio-item" v-if="dettaglioSelezionato.dati?.straordinari > 0">
          <h5>{{ $root.trans.straordinari }}</h5>

          <div v-for="dettaglio in getDettaglioStraordinariConfigurato(dettaglioSelezionato.dati.dettaglio_straordinari)"
               :key="dettaglio.categoria"
               class="straordinario-categoria">
            <label>{{ dettaglio.nome }}:</label>
            <span class="tempo-straordinario">{{ dettaglio.tempo }}</span>
            <small v-if="dettaglio.approvati > 0" class="approvati">({{ dettaglio.approvati }} min approvati)</small>
          </div>
        </div>

        <div class="dettaglio-item" v-if="dettaglioSelezionato.dati?.assenze > 0">
          <label>{{ $root.trans.assenze }}:</label>
          <span class="badge badge-warning">{{ $root.trans.si }}</span>
        </div>

        <div class="dettaglio-item" v-if="dettaglioSelezionato.dati?.avvisi > 0">
          <label>{{ $root.trans.avvisi }}:</label>
          <span class="badge badge-danger" v-html="dettaglioSelezionato.dati?.descrizione_avvisi"></span>
        </div>

        <div class="dettaglio-item" v-if="dettaglioSelezionato.dati?.smartworking > 0">
          <label>{{ $root.trans.smartworking }}:</label>
          <span class="badge badge-danger">{{ $root.trans.si }}</span>
        </div>

        <div class="dettaglio-item">
          <label>{{ $root.trans.stato }}:</label>
          <span :class="getStatoClass(dettaglioSelezionato.dati?.confermato)">
            {{ dettaglioSelezionato.dati?.confermato > 0 ? $root.trans.confermato : $root.trans.non_confermato }}
          </span>
        </div>

        <!-- Dettaglio timbrature se disponibile -->
        <div v-if="dettaglioTimbrature && dettaglioTimbrature.length > 0" class="mt-4">
          <h5>{{ $root.trans.timbrature }}:</h5>

          <div v-if="hasCodiceT003()" class="alert-box warning mb-3">
            <i class="pi pi-exclamation-triangle"></i>
            {{ $root.trans.timing_aler_verifica_timbrature }}
          </div>
          <div v-if="dettaglioSelezionato.dati?.modifiche == 1" class="alert-box warning mb-3">
            <i class="pi pi-exclamation-triangle"></i>
            {{ $root.trans.alert_timbratura_in_attesa_di_verifica }}
          </div>

          <ul class="timbrature-list">
            <li v-for="(timbratura, index) in dettaglioTimbrature" :key="index">
              <span v-html="icona_errore(timbratura.errore,1)"></span><span v-if="timbratura.ingresso_id > 0">{{ formatIntervalloTimbratura(timbratura.ingresso_dataora, timbratura.uscita_dataora)[0] }}</span>
              -
              <span v-html="icona_errore(timbratura.errore,2)"></span><span v-if="timbratura.uscita_id > 0"> {{ formatIntervalloTimbratura(timbratura.ingresso_dataora, timbratura.uscita_dataora)[1] }}</span>
              <br/>
              <span v-if="timbratura.ingresso_id > 0">{{ $root.trans.cliente_ingresso }} {{ timbratura.cliente_ingresso != '' ?  timbratura.cliente_ingresso : "EXTRA" }}</span><br/>
              <span v-if="timbratura.uscita_id > 0">{{ $root.trans.cliente_uscita }} {{ timbratura.cliente_uscita != '' ?  timbratura.cliente_uscita : "EXTRA" }}</span><br/>
              <span v-if="timbratura.ingresso_id > 0">{{ $root.trans.ingresso_indirizzo }} {{ timbratura.ingresso_indirizzo != '' ?  timbratura.ingresso_indirizzo : timbratura.ingresso_coordinate }}</span><br/>
              <span v-if="timbratura.uscita_id > 0">{{ $root.trans.uscita_indirizzo }} {{ timbratura.uscita_indirizzo != '' ?  timbratura.uscita_indirizzo : timbratura.uscita_coordinate }}</span><br/>
              <span v-if="timbratura.stato_ingresso == -1 || timbratura.stato_uscita == -1">{{ $root.trans.timbratura_in_attesa_di_verifica }}</span><br/>
              <span v-html="stampa_errore(timbratura.errore)"></span>
              <button v-if="timbratura.errore != '' && !['T002|T002', 'T002', 'T002|'].includes(timbratura.errore)" class="p-button p-button-sm p-button-info mt-2" @click="verificaLettura(timbratura,dettaglioSelezionato.dati.timbrature_cantieri_diversi)">{{ $root.trans.verifica_lettura }}</button>
              <br/>
            </li>
          </ul>
        </div>
      </div>
    </Drawer>

    <!-- Dialog per conferma giornata -->
    <Dialog v-model:visible="confermaGiornataDialog" :header="$root.trans.conferma_giornata" :modal="true" :style="{ width: '500px' }">
      <div v-if="dettaglioSelezionato" class="conferma-giornata-content">
        <div class="info-giornata mb-4">
          {{ dettaglioSelezionato }}
          <h5>{{ formatDayName(dettaglioSelezionato.data) }} {{ formatDate(dettaglioSelezionato.data) }}</h5>
          <p><strong>{{ dettaglioSelezionato.dipendente }}</strong></p>

          <div class="dettaglio-riepilogo mt-3">
            <div class="info-row">
              <span>{{ $root.trans.ore_lavorate }}:</span>
              <strong>{{ dettaglioSelezionato.dati?.minuti || '0' }}</strong>
            </div>
            <div class="info-row">
              <span>{{ $root.trans.ore_contratto }}:</span>
              <strong>{{ dettaglioSelezionato.dati?.ore_contratto || '0' }}</strong>
            </div>
          </div>
        </div>

        <!-- Pulsanti azioni -->
        <div class="flex flex-column gap-2">
          <!-- Mostra solo CONFERMA se minuti > ore_contratto*60 -->
          <template v-if="minutiMaggioriDiContratto()">
            <Button
              :label="$root.trans.conferma"
              severity="success"
              @click="handleConferma"
              :loading="loadingConfermaGiornata"
              icon="pi pi-check"
              class="w-full"
            />
          </template>

          <!-- Mostra COMPLETA INCOMPLETA e COMPLETA CON ASSENZA se minuti < ore_contratto*60 -->
          <template v-else>
            <Button
              label="Completa giornata incompleta"
              severity="info"
              @click="handleCompletaIncompleta"
              :loading="loadingConfermaGiornata"
              icon="pi pi-clock"
              class="w-full"
            />
            <Button
              label="Completa giornata con assenza"
              severity="warning"
              @click="apriSelezioneAssenza"
              :loading="loadingConfermaGiornata"
              icon="pi pi-calendar-times"
              class="w-full"
            />
          </template>

          <!-- Mostra COMPENSA solo se compensazione != 0 -->
          <Button
            v-if="dettaglioSelezionato.dati?.compensazione != 0"
            label="Compensa"
            severity="help"
            @click="handleCompensa"
            :loading="loadingConfermaGiornata"
            icon="pi pi-sync"
            class="w-full"
          />

          <Button
            :label="$root.trans.annulla"
            severity="secondary"
            @click="chiudiConfermaGiornata"
            :disabled="loadingConfermaGiornata"
            icon="pi pi-times"
            class="w-full"
          />
        </div>
      </div>
    </Dialog>

    <!-- Dialog per selezione assenza -->
    <Dialog v-model:visible="assenzaSelezionataDialog" header="Seleziona Assenza" :modal="true" :style="{ width: '500px' }">
      <div class="selezione-assenza-content">
        <div class="mb-3">
          <label class="block mb-2">Tipologia Assenza</label>
          <Dropdown
            v-model="tipologiaAssenzaSelezionata"
            :options="tipologie_assenza"
            optionLabel="nome"
            optionValue="id"
            :placeholder="$root.trans.seleziona_tipologia"
            class="w-full"
            @change="onTipologiaChange"
          />
        </div>

        <div class="mb-3" v-if="tipologiaAssenzaSelezionata">
          <label class="block mb-2">Assenza</label>
          <Dropdown
            v-model="assenzaSelezionata"
            :options="getAssenzePerTipologia()"
            optionLabel="nome"
            optionValue="id"
            :placeholder="$root.trans.seleziona_assenza"
            class="w-full"
          />
        </div>

        <div class="flex gap-2 justify-end mt-4">
          <Button
            :label="$root.trans.annulla"
            severity="secondary"
            @click="chiudiSelezioneAssenza"
            icon="pi pi-times"
          />
          <Button
            label="Conferma"
            severity="success"
            @click="handleCompletaAssenza"
            :disabled="!assenzaSelezionata"
            :loading="loadingConfermaGiornata"
            icon="pi pi-check"
          />
        </div>
      </div>
    </Dialog>

    <!-- Dialog per verifica timbrature -->
    <Dialog v-model:visible="verificaTimbrature" :header="$root.trans.verifica_lettura" :modal="true" :style="{ width: '600px' }">
      <div v-if="timbraturaCorrente" class="verifica-content">
        <div class="timbratura-info mb-4">
          <div class="info-row">
            <strong>{{ $root.trans.ingresso }}:</strong>
            <span v-html="icona_errore(timbraturaCorrente.errore, 1)"></span>
            <span v-if="timbraturaCorrente.ingresso_id > 0">
              {{ formatIntervalloTimbratura(timbraturaCorrente.ingresso_dataora, timbraturaCorrente.uscita_dataora)[0] }}
            </span>
          </div>

          <div class="info-row">
            <strong>{{ $root.trans.uscita }}:</strong>
            <span v-html="icona_errore(timbraturaCorrente.errore, 2)"></span>
            <span v-if="timbraturaCorrente.uscita_id > 0">
              {{ formatIntervalloTimbratura(timbraturaCorrente.ingresso_dataora, timbraturaCorrente.uscita_dataora)[1] }}
            </span>
          </div>

          <div class="info-row" v-if="timbraturaCorrente.ingresso_id > 0">
            <strong>{{ $root.trans.cliente_ingresso }}:</strong>
            {{ timbraturaCorrente.cliente_ingresso != '' ? timbraturaCorrente.cliente_ingresso : "EXTRA" }}
          </div>

          <div class="info-row" v-if="timbraturaCorrente.uscita_id > 0">
            <strong>{{ $root.trans.cliente_uscita }}:</strong>
            {{ timbraturaCorrente.cliente_uscita != '' ? timbraturaCorrente.cliente_uscita : "EXTRA" }}
          </div>

          <div class="info-row" v-if="timbraturaCorrente.ingresso_id > 0">
            <strong>{{ $root.trans.ingresso_indirizzo }}:</strong>
            {{ timbraturaCorrente.ingresso_indirizzo != '' ? timbraturaCorrente.ingresso_indirizzo : timbraturaCorrente.ingresso_coordinate }}
          </div>

          <div class="info-row" v-if="timbraturaCorrente.uscita_id > 0">
            <strong>{{ $root.trans.uscita_indirizzo }}:</strong>
            {{ timbraturaCorrente.uscita_indirizzo != '' ? timbraturaCorrente.uscita_indirizzo : timbraturaCorrente.uscita_coordinate }}
          </div>

          <div class="alert-box warning mt-3" v-if="timbraturaCorrente.errore">
            <div v-html="stampa_errore(timbraturaCorrente.errore)"></div>
          </div>
        </div>

        <!-- Pulsanti azioni -->
        <template v-if="timbraturaCantieriDifferenti == 1">
          <div class="flex gap-2 justify-end" v-if="!['T002|T002', 'T002', 'T002|'].includes(timbraturaCorrente.errore)">
            <Button
              :label="$root.trans.verificata"
              severity="success"
              @click="confermaTimbratura"
              :loading="loadingVerifica"
              icon="pi pi-check"
            />
          </div>
        </template>
        <template v-else>
          <div class="flex gap-2 justify-end" v-if="timbraturaCorrente.errore && timbraturaCorrente.errore.includes('T006') && timbraturaCorrente.errore.includes('T007')">
            <Button
              :label="$root.trans.assegna_cliente"
              severity="success"
              @click="assegnaCliente"
              :loading="loadingVerifica"
              icon="pi pi-check"
            />
          </div>
          <div class="flex gap-2 justify-end" v-if="timbraturaCorrente.errore && (
            (timbraturaCorrente.ingresso_qrcode != '' && timbraturaCorrente.uscita_qrcode == '') ||
            (timbraturaCorrente.ingresso_qrcode == '' && timbraturaCorrente.uscita_qrcode != '')
        )">
            <Button
              :label="$root.trans.assegna_stesso_cliente"
              severity="success"
              @click="assegnaStessoCliente"
              :loading="loadingVerifica"
              icon="pi pi-check"
            />
          </div>
        </template>
      </div>
    </Dialog>

    <!-- Dialog per assegnazione QR Code -->
    <Dialog
      v-model:visible="qrcodeDialog"
      header="Assegna Cliente"
      :modal="true"
      :style="{ width: '500px' }"
    >
      <div v-if="timbraturaCorrente" class="qrcode-content">
        <div class="mb-4">
          <p class="text-sm text-gray-600 mb-3">
            Seleziona il cliente da assegnare alla timbratura
          </p>

          <!-- Select unica se cantieri diversi = 0 -->
          <div v-if="timbraturaCanteriDiversi === 0" class="mb-3">
            <label class="block mb-2 font-semibold">Cliente</label>
            <Dropdown
              v-model="qrcodeSelezionato"
              :options="qrcodeList"
              optionLabel="nome"
              optionValue="id"
              :placeholder="$root.trans.seleziona_cliente"
              class="w-full"
            />
          </div>

          <!-- Due select se cantieri diversi = 0 -->
          <template v-else>
            <div class="mb-3">
              <label class="block mb-2 font-semibold">Cliente Ingresso</label>
              <Dropdown
                v-model="qrcodeIngressoSelezionato"
                :options="qrcodeList"
                optionLabel="nome"
                optionValue="id"
                :placeholder="$root.trans.seleziona_cliente_ingresso"
                class="w-full"
              />
            </div>

            <div class="mb-3">
              <label class="block mb-2 font-semibold">Cliente Uscita</label>
              <Dropdown
                v-model="qrcodeUscitaSelezionato"
                :options="qrcodeList"
                optionLabel="nome"
                optionValue="id"
                :placeholder="$root.trans.seleziona_cliente_uscita"
                class="w-full"
              />
            </div>
          </template>
        </div>

        <div class="flex gap-2 justify-end">
          <Button
            :label="$root.trans.annulla"
            severity="secondary"
            @click="chiudiQrcodeDialog"
            icon="pi pi-times"
            :disabled="loadingQrcode"
          />
          <Button
            label="Salva e verifica"
            severity="success"
            @click="salvaEVerifica"
            :loading="loadingQrcode"
            icon="pi pi-check"
          />
        </div>
      </div>
    </Dialog>

    <!-- Overlay per chiudere il DatePicker cliccando fuori -->
    <div
      v-if="showDatePicker"
      class="date-picker-overlay-bg"
      @click="closeDatePicker"
    ></div>

    <!-- Dialog per modifica timbrature con drag&drop -->
    <!-- Sostituire la sezione del dialog modificaTimbratureDialog esistente -->

    <!-- Dialog per modifica timbrature con drag&drop -->
    <Dialog
      v-model:visible="modificaTimbratureDialog"
      :header="dettaglioSelezionato && dettaglioSelezionato.dipendente ? `${dettaglioSelezionato.dipendente} - ${$root.trans.modifica_timbrature}` : ''"
      :modal="true"
      :style="{ width: '90vw', maxWidth: '1200px' }"
      :maximizable="true"
    >
      <div v-if="timbraturaGriglia" class="modifica-timbrature-content">
        <div class="mb-3 flex justify-between items-center">
          {{ dettaglioSelezionato }}
          <h5 class="m-0">{{ formatDayName(dettaglioSelezionato.data) }} {{ formatDate(dettaglioSelezionato.data) }}</h5>
          <Button
            :label="$root.trans.aggiungi_riga"
            icon="pi pi-plus"
            @click="aggiungiRigaTimbratura"
            class="p-button-sm p-button-success"
          />
        </div>

        <!-- Griglia timbrature -->
        <div class="timbrature-grid-container">
          <table class="timbrature-grid">
            <thead>
            <tr>
              <th style="width: 50px">#</th>
              <th style="width: 42%">{{ $root.trans.ingresso }}</th>
              <th style="width: 42%">{{ $root.trans.uscita }}</th>
              <th style="width: 100px">{{ $root.trans.azioni }}</th>
            </tr>
            </thead>
            <tbody>
            <tr
              v-for="(riga, index) in timbraturaGriglia"
              :key="riga.id || index"
              class="timbratura-row"
              :class="{
          'riga-eliminata': riga.eliminata,
          'riga-parzialmente-eliminata': !riga.eliminata && ((riga.ingresso?.isDeleted) || (riga.uscita?.isDeleted))
        }"
            >
              <td class="row-number" :class="{ 'eliminata': riga.eliminata }">
                {{ index + 1 }}
                <small v-if="riga.eliminata" class="eliminata-label">ELIMINATA</small>
              </td>

              <!-- Cella Ingresso -->
              <td
                class="timbratura-cell"
                :class="{
            'has-data': riga.ingresso,
            'drag-over': dragOverCell === `${index}-ingresso`,
            'eliminata': riga.eliminata,
            'timbratura-eliminata': riga.ingresso?.isDeleted && !riga.eliminata
          }"
                @drop="!riga.eliminata && !riga.ingresso?.isDeleted && onDrop($event, index, 'ingresso')"
                @dragover.prevent="!riga.eliminata && !riga.ingresso?.isDeleted && onDragOver($event, index, 'ingresso')"
                @dragleave="onDragLeave"
              >
                <div
                  v-if="riga.ingresso"
                  class="timbratura-item"
                  :class="{
              'has-error': riga.ingresso.errore,
              'eliminata': riga.eliminata,
              'timbratura-eliminata': riga.ingresso.isDeleted && !riga.eliminata,
              'modificata': riga.ingresso.isModified,
              'nuova': riga.ingresso.isNew
            }"
                  :draggable="!riga.eliminata && !riga.ingresso.isDeleted"
                  @dragstart="!riga.eliminata && !riga.ingresso.isDeleted && onDragStart($event, index, 'ingresso', riga.ingresso)"
                  @dragend="onDragEnd"
                >
                  <div class="timbratura-info">
                    <div class="timbratura-time">{{ riga.ingresso }}
                      <span v-if="riga.ingresso.errore" v-html="icona_errore(riga.ingresso.errore, 1)"></span>
                      <i class="pi pi-clock"></i>
                      {{ formatOrario(riga.ingresso.dataora) }}
                      <!-- Badge per stato -->
                      <span v-if="riga.ingresso.isNew" class="status-badge nuova">NUOVO</span>
                      <span v-else-if="riga.ingresso.isModified" class="status-badge modificata">MODIFICATO</span>
                      <span v-else-if="riga.ingresso.isDeleted" class="status-badge eliminata">ELIMINATO</span>
                    </div>
                    <div class="timbratura-cliente">
                      <i class="pi pi-briefcase"></i>
                      {{ riga.ingresso.cliente || 'EXTRA' }}
                    </div>
                    <div class="timbratura-location" v-if="riga.ingresso.indirizzo">
                      <i class="pi pi-map-marker"></i>
                      {{ riga.ingresso.indirizzo }}
                    </div>
                    <div v-if="riga.ingresso.errore" class="timbratura-errore">
                      <span v-html="stampa_errore(riga.ingresso.errore)"></span>
                    </div>
                  </div>

                  <!-- Pulsanti azione per la singola timbratura -->
                  <div class="timbratura-actions" v-if="!riga.eliminata">
                    <!-- Pulsante modifica (solo se non eliminata) -->
                    <Button
                      v-if="!riga.ingresso.isDeleted"
                      icon="pi pi-pencil"
                      class="p-button-rounded p-button-text p-button-sm p-button-info edit-btn"
                      @click="apriModificaSingolaTimbratura(index, 'ingresso')"
                      :title="$root.trans.modifica_ingresso || 'Modifica ingresso'"
                    />

                    <!-- Pulsante elimina/ripristina -->
                    <Button
                      v-if="riga.ingresso.isNew"
                      icon="pi pi-times"
                      class="p-button-rounded p-button-text p-button-sm p-button-danger delete-btn"
                      @click="toggleEliminazioneTimbratura(index, 'ingresso')"
                      :title="$root.trans.elimina_timbratura || 'Elimina timbratura'"
                    />
                    <Button
                      v-else-if="riga.ingresso.isDeleted"
                      icon="pi pi-undo"
                      class="p-button-rounded p-button-text p-button-sm p-button-success restore-btn"
                      @click="toggleEliminazioneTimbratura(index, 'ingresso')"
                      :title="$root.trans.ripristina_timbratura || 'Ripristina timbratura'"
                    />
                    <Button
                      v-else
                      icon="pi pi-trash"
                      class="p-button-rounded p-button-text p-button-sm p-button-warning delete-btn"
                      @click="toggleEliminazioneTimbratura(index, 'ingresso')"
                      :title="$root.trans.elimina_timbratura || 'Elimina timbratura'"
                    />
                  </div>
                </div>
                <div v-else-if="!riga.eliminata" class="empty-cell">
                  <Button
                    icon="pi pi-plus"
                    class="p-button-rounded p-button-text add-btn"
                    @click="apriInserimentoTimbratura(index, 'ingresso')"
                    :title="$root.trans.aggiungi_ingresso || 'Aggiungi ingresso'"
                  />
                </div>
                <div v-else class="empty-cell eliminata">
                  <span class="eliminata-placeholder">Timbratura eliminata</span>
                </div>
              </td>

              <!-- Cella Uscita -->
              <td
                class="timbratura-cell"
                :class="{
            'has-data': riga.uscita,
            'drag-over': dragOverCell === `${index}-uscita`,
            'eliminata': riga.eliminata,
            'timbratura-eliminata': riga.uscita?.isDeleted && !riga.eliminata
          }"
                @drop="!riga.eliminata && !riga.uscita?.isDeleted && onDrop($event, index, 'uscita')"
                @dragover.prevent="!riga.eliminata && !riga.uscita?.isDeleted && onDragOver($event, index, 'uscita')"
                @dragleave="onDragLeave"
              >
                <div
                  v-if="riga.uscita"
                  class="timbratura-item"
                  :class="{
              'has-error': riga.uscita.errore,
              'eliminata': riga.eliminata,
              'timbratura-eliminata': riga.uscita.isDeleted && !riga.eliminata,
              'modificata': riga.uscita.isModified,
              'nuova': riga.uscita.isNew
            }"
                  :draggable="!riga.eliminata && !riga.uscita.isDeleted"
                  @dragstart="!riga.eliminata && !riga.uscita.isDeleted && onDragStart($event, index, 'uscita', riga.uscita)"
                  @dragend="onDragEnd"
                >
                  <div class="timbratura-info">
                    <div class="timbratura-time">
                      <span v-if="riga.uscita.errore" v-html="icona_errore(riga.uscita.errore, 2)"></span>
                      <i class="pi pi-clock"></i>
                      {{ formatOrario(riga.uscita.dataora) }}
                      <!-- Badge per stato -->
                      <span v-if="riga.uscita.isNew" class="status-badge nuova">NUOVO</span>
                      <span v-else-if="riga.uscita.isModified" class="status-badge modificata">MODIFICATO</span>
                      <span v-else-if="riga.uscita.isDeleted" class="status-badge eliminata">ELIMINATO</span>
                    </div>
                    <div class="timbratura-cliente">
                      <i class="pi pi-briefcase"></i>
                      {{ riga.uscita.cliente || 'EXTRA' }}
                    </div>
                    <div class="timbratura-location" v-if="riga.uscita.indirizzo">
                      <i class="pi pi-map-marker"></i>
                      {{ riga.uscita.indirizzo }}
                    </div>
                    <div v-if="riga.uscita.errore" class="timbratura-errore">
                      <span v-html="stampa_errore(riga.uscita.errore)"></span>
                    </div>
                  </div>

                  <!-- Pulsanti azione per la singola timbratura -->
                  <div class="timbratura-actions" v-if="!riga.eliminata">
                    <!-- Pulsante modifica (solo se non eliminata) -->
                    <Button
                      v-if="!riga.uscita.isDeleted"
                      icon="pi pi-pencil"
                      class="p-button-rounded p-button-text p-button-sm p-button-info edit-btn"
                      @click="apriModificaSingolaTimbratura(index, 'uscita')"
                      :title="$root.trans.modifica_uscita || 'Modifica uscita'"
                    />

                    <!-- Pulsante elimina/ripristina -->
                    <Button
                      v-if="riga.uscita.isNew"
                      icon="pi pi-times"
                      class="p-button-rounded p-button-text p-button-sm p-button-danger delete-btn"
                      @click="toggleEliminazioneTimbratura(index, 'uscita')"
                      :title="$root.trans.elimina_timbratura || 'Elimina timbratura'"
                    />
                    <Button
                      v-else-if="riga.uscita.isDeleted"
                      icon="pi pi-undo"
                      class="p-button-rounded p-button-text p-button-sm p-button-success restore-btn"
                      @click="toggleEliminazioneTimbratura(index, 'uscita')"
                      :title="$root.trans.ripristina_timbratura || 'Ripristina timbratura'"
                    />
                    <Button
                      v-else
                      icon="pi pi-trash"
                      class="p-button-rounded p-button-text p-button-sm p-button-warning delete-btn"
                      @click="toggleEliminazioneTimbratura(index, 'uscita')"
                      :title="$root.trans.elimina_timbratura || 'Elimina timbratura'"
                    />
                  </div>
                </div>
                <div v-else-if="!riga.eliminata" class="empty-cell">
                  <Button
                    icon="pi pi-plus"
                    class="p-button-rounded p-button-text add-btn"
                    @click="apriInserimentoTimbratura(index, 'uscita')"
                    :title="$root.trans.aggiungi_uscita || 'Aggiungi uscita'"
                  />
                </div>
                <div v-else class="empty-cell eliminata">
                  <span class="eliminata-placeholder">Timbratura eliminata</span>
                </div>
              </td>

              <!-- Azioni per la riga -->
              <td class="actions-cell" :class="{ 'eliminata': riga.eliminata }">
                <div class="actions-column">
                  <!-- Pulsante ripristina per righe eliminate con timbrature esistenti -->
                  <Button
                    v-if="riga.eliminata || (hasTimbratureEliminate(riga) && !hasTimbratureAttive(riga))"
                    icon="pi pi-undo"
                    class="p-button-rounded p-button-text p-button-sm p-button-success"
                    @click="ripristinaRiga(index)"
                    :title="$root.trans.ripristina || 'Ripristina'"
                  />

                  <!-- Pulsante elimina per righe non eliminate -->
                  <Button
                    v-else
                    icon="pi pi-trash"
                    class="p-button-rounded p-button-text p-button-sm p-button-danger"
                    @click="confermaEliminazioneRiga(index)"
                    :disabled="timbraturaGriglia.length === 1"
                    :title="$root.trans.elimina_coppia || 'Elimina coppia timbratura'"
                  />

                  <!-- Indicatore se ci sono modifiche -->
                  <small v-if="rigaHaModifiche(riga)" class="modified-indicator">
                    <i class="pi pi-exclamation-circle text-warning"></i>
                    <span v-if="riga.eliminata">Eliminata</span>
                    <span v-else-if="hasTimbratureEliminate(riga) && !hasTimbratureAttive(riga)">Timbrature eliminate</span>
                    <span v-else-if="hasTimbratureEliminate(riga) && hasTimbratureAttive(riga)">Parziale</span>
                    <span v-else-if="riga.posizioneOriginale !== null && riga.posizioneOriginale !== index">Spostata</span>
                    <span v-else>Modificata</span>
                  </small>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Pulsanti azioni -->
        <div class="flex gap-2 justify-end mt-4">
          <Button
            :label="$root.trans.annulla"
            severity="secondary"
            @click="chiudiModificaTimbrature"
            icon="pi pi-times"
            :disabled="loadingSalvaTimbrature"
          />
          <Button
            label="Salva Modifiche"
            severity="success"
            @click="salvaModificheTimbrature"
            :loading="loadingSalvaTimbrature"
            icon="pi pi-check"
          />
        </div>
      </div>
    </Dialog>

    <!-- Dialog per modifica singola timbratura -->
    <Dialog
      v-model:visible="modificaSingolaDialog"
      :header="tipoModificaSingola === 'ingresso' ? 'Modifica Ingresso' : 'Modifica Uscita'"
      :modal="true"
      :style="{ width: '500px' }"
    >
      <div v-if="timbraturaInModifica" class="modifica-singola-content">
        <div class="mb-3">
          <label class="block mb-2 font-semibold">{{ $root.trans.data_e_ora }}</label>
          <DatePicker
            v-model="timbraturaModificata.dataora"
            showTime
            hourFormat="24"
            dateFormat="dd/mm/yy"
            :placeholder="$root.trans.seleziona_data_ora"
            class="w-full"
          />
        </div>

        <div class="mb-3">
          <label class="block mb-2 font-semibold">{{ $root.trans.cliente }}</label>
          <Dropdown
            v-model="timbraturaModificata.cliente_id"
            :options="qrcodeList"
            optionLabel="nome"
            optionValue="id"
            :placeholder="$root.trans.seleziona_cliente"
            class="w-full"
            showClear
          />
          <small class="text-gray-500">Lascia vuoto per timbratura EXTRA</small>
        </div>


        <div class="flex gap-2 justify-end mt-4">
          <Button
            :label="$root.trans.annulla"
            severity="secondary"
            @click="chiudiModificaSingola"
            icon="pi pi-times"
            :disabled="loadingModificaSingola"
          />
          <Button
            label="Salva Modifiche"
            severity="success"
            @click="salvaModificaSingola"
            :loading="loadingModificaSingola"
            icon="pi pi-check"
          />
        </div>
      </div>
    </Dialog>

    <!-- Dialog di conferma eliminazione riga -->
    <Dialog
      v-model:visible="confermaEliminazioneDialog"
      header="Conferma Eliminazione"
      :modal="true"
      :style="{ width: '400px' }"
    >
      <div class="conferma-eliminazione-content">
        <div class="flex items-center gap-3 mb-4">
          <i class="pi pi-exclamation-triangle text-yellow-500 text-2xl"></i>
          <div>
            <p class="m-0 font-semibold">{{ $root.trans.elimina_coppia_timbrature }}</p>
            <small class="text-gray-600">Questa azione può essere annullata.</small>
          </div>
        </div>

        <div class="flex gap-2 justify-end">
          <Button
            :label="$root.trans.annulla"
            severity="secondary"
            @click="annullaEliminazioneRiga"
            icon="pi pi-times"
          />
          <Button
            label="Elimina"
            severity="danger"
            @click="confermaEliminazioneDefinitiva"
            icon="pi pi-trash"
          />
        </div>
      </div>
    </Dialog>

    <!-- Dialog per inserimento singola timbratura -->
    <Dialog
      v-model:visible="inserimentoTimbrataDialog"
      :header="tipoInserimento === 'ingresso' ? 'Inserisci Ingresso' : 'Inserisci Uscita'"
      :modal="true"
      :style="{ width: '500px' }"
    >
      <div class="inserimento-timbratura-content">
        <div class="mb-3">
          <label class="block mb-2 font-semibold">Data e Ora</label>
          <DatePicker
            v-model="nuovaTimbratura.dataora"
            showTime
            hourFormat="24"
            dateFormat="dd/mm/yy"
            :placeholder="$root.trans.seleziona_data_ora || 'Seleziona data e ora'"
            class="w-full"
          />
        </div>

        <div class="mb-3">
          <label class="block mb-2 font-semibold">Cliente</label>
          <Dropdown
            v-model="nuovaTimbratura.cliente_id"
            :options="qrcodeList"
            optionLabel="nome"
            optionValue="id"
            :placeholder="$root.trans.seleziona_cliente || 'Seleziona cliente'"
            class="w-full"
            showClear
          />
          <small class="text-gray-500">Lascia vuoto per timbratura EXTRA</small>
        </div>
        <div class="flex gap-2 justify-end mt-4">
          <Button
            :label="$root.trans.annulla"
            severity="secondary"
            @click="chiudiInserimentoTimbratura"
            icon="pi pi-times"
          />
          <Button
            label="Aggiungi"
            severity="success"
            @click="confermaInserimentoTimbratura"
            :disabled="!nuovaTimbratura.dataora"
            icon="pi pi-check"
          />
        </div>
      </div>
    </Dialog>

  </div>
</template>

<script>
import axios from 'axios';
import Drawer from 'primevue/drawer';
import DatePicker from 'primevue/datepicker';
import MultiSelect from 'primevue/multiselect';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import Toast from 'primevue/toast';
import Dialog from 'primevue/dialog';

export default {
  name: 'TimingComponent',
  components: {
    Drawer,
    DatePicker,
    MultiSelect,
    Dropdown,
    Button,
    ProgressSpinner,
    Toast,
    Dialog
  },
  props: {
    datigriglia: {
      type: Object,
      default: () => ({})
    },
    intestazioni: {
      type: Array,
      default: () => []
    },
    timing_scelto: {
      type: String,
      default: ''
    },
    data_inizio: {
      type: String,
      default: ''
    },
    data_fine: {
      type: String,
      default: ''
    },
    dipendenti: {
      type: Array,
      default: () => []
    },
    dipendenti_selezionati: {
      type: Array,
      default: () => []
    },
    tipologie_assenza: {
      type: Array,
      default: () => []
    },
    assenze: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      drawerVisible: false,
      dettaglioSelezionato: null,
      dettaglioTimbrature: null,
      dipendentiSelezionati: [],
      dataCentrale: null,
      periodoAttuale: {
        inizio: null,
        fine: null
      },
      loading: false,
      datigrigliaInterna: {},
      intestazioniInterne: [],
      showDatePicker: false,
      timingSceltoInterno: null,
      opzioniVisualizzazione: [
        { label: this.$root.trans.vista_settimanale, value: 1 },
        { label: this.$root.trans.vista_2_settimane, value: 2 }
      ],
      categorieStraordinari : {
        0 : this.$root.trans.straordinario_non_richiesto,
        1 : this.$root.trans.straordinario_inserito,
        2 : this.$root.trans.straordinario_richiesto,
        3 : this.$root.trans.straordinario_approvato,
        4 : this.$root.trans.straordinario_rifiutato,
        5 : this.$root.trans.straordinario_scaduto
      },
      verificaTimbrature: false,
      timbraturaCorrente: null,
      loadingVerifica: false,
      confermaGiornataDialog: false,
      loadingConfermaGiornata: false,
      assenzaSelezionataDialog: false,
      tipologiaAssenzaSelezionata: null,
      assenzaSelezionata: null,
      oreDaCompensare: 0,
      messaggioCompensazione: '',
      qrcodeDialog: false,
      qrcodeList: [],
      timbraturaCanteriDiversi: 0,
      qrcodeSelezionato: null,
      qrcodeIngressoSelezionato: null,
      qrcodeUscitaSelezionato: null,
      loadingQrcode: false,
      modificaTimbratureDialog: false,
      timbraturaGriglia: [],
      draggedItem: null,
      dragOverCell: null,
      loadingSalvaTimbrature: false,
      inserimentoTimbrataDialog: false,
      rigaInserimento: null,
      tipoInserimento: null,
      nuovaTimbratura: {
        dataora: null,
        cliente_id: null,
      },
      modificaSingolaDialog: false,
      timbraturaInModifica: null,
      rigaInModifica: null,
      tipoModificaSingola: null, // 'ingresso' o 'uscita'
      timbraturaModificata: {
        dataora: null,
        cliente_id: null,
      },
      loadingModificaSingola: false,
      confermaEliminazioneDialog: false,
      rigaDaEliminare: null,
      timbraturaGrigliaOriginale: [], // Per tracciare lo stato iniziale
      contatoreTempId: -1,
    }
  },
  mounted() {
    this.inizializzaFiltri();
    this.datigrigliaInterna = JSON.parse(JSON.stringify(this.datigriglia));
    this.intestazioniInterne = JSON.parse(JSON.stringify(this.intestazioni));
    this.timingSceltoInterno = this.timing_scelto;
  },
  computed: {
    dipendentiFormattati() {
      return this.dipendenti.map(d => ({
        ...d,
        cognome_nome: `${d.cognome} ${d.nome}`
      }));
    }
  },
  methods: {
    showToast(severity, summary, detail) {
      if (this.$toast) {
        this.$toast.add({
          severity: severity,
          summary: summary,
          detail: detail,
          life: 4000
        });
      } else {
        console.warn(`TOAST ${severity.toUpperCase()}: ${summary} - ${detail}`);
        alert(`${summary}: ${detail}`);
      }
    },
    inizializzaFiltri() {
      if (this.data_inizio && this.data_fine) {
        const dataInizio = new Date(this.data_inizio);
        const dataFine = new Date(this.data_fine);
        const differenzaMs = dataFine.getTime() - dataInizio.getTime();
        const giornoMedio = new Date(dataInizio.getTime() + (differenzaMs / 2));
        this.dataCentrale = giornoMedio;
        this.calcolaIntervalloData(giornoMedio);
      } else {
        this.impostaOggiCentrale();
      }
      this.dipendentiSelezionati = [...this.dipendenti_selezionati];
    },
    impostaOggiCentrale() {
      const oggi = new Date();
      this.dataCentrale = oggi;
      this.calcolaIntervalloData(oggi);
    },
    calcolaIntervalloData(dataCentrale) {
      const dataInizio = new Date(dataCentrale);
      const dataFine = new Date(dataCentrale);
      const giorni = this.timingSceltoInterno === 1 ? 7 : 14;
      const giorniPrima = Math.floor(giorni / 2);
      const giorniDopo = giorni - giorniPrima - 1;
      dataInizio.setDate(dataCentrale.getDate() - giorniPrima);
      dataFine.setDate(dataCentrale.getDate() + giorniDopo);
      this.periodoAttuale.inizio = dataInizio;
      this.periodoAttuale.fine = dataFine;
    },
    cambiaVisualizzazione() {
      console.log('Cambiata visualizzazione a:', this.timingSceltoInterno);
      if (this.dataCentrale) {
        this.calcolaIntervalloData(this.dataCentrale);
        this.caricaDati();
      }
    },
    toggleDatePicker() {
      if (this.loading) return;
      this.showDatePicker = !this.showDatePicker;
    },
    closeDatePicker() {
      this.showDatePicker = false;
    },
    onDateSelect(date) {
      console.log('Data selezionata:', date);
      if (date) {
        this.dataCentrale = date;
        this.calcolaIntervalloData(date);
        this.closeDatePicker();
        this.caricaDati();
      }
    },
    formatIntervalloDisplay() {
      if (!this.periodoAttuale.inizio || !this.periodoAttuale.fine) {
        return '';
      }
      const opzioni = { day: '2-digit', month: '2-digit', year: 'numeric' };
      const inizioStr = this.periodoAttuale.inizio.toLocaleDateString('it-IT', opzioni);
      const fineStr = this.periodoAttuale.fine.toLocaleDateString('it-IT', opzioni);
      return `${inizioStr} - ${fineStr}`;
    },
    vaiAOggi() {
      if (this.loading) return;
      this.impostaOggiCentrale();
      this.caricaDati();
    },
    isGiornoCorrente() {
      if (!this.dataCentrale) return false;
      const oggi = new Date();
      return this.dataCentrale.toDateString() === oggi.toDateString();
    },
    formatMeseAnno() {
      if (!this.periodoAttuale.inizio || !this.periodoAttuale.fine) {
        return '';
      }
      const dataInizio = this.periodoAttuale.inizio;
      const dataFine = this.periodoAttuale.fine;
      const nomiMesi = [
        this.$root.trans.mesi?.gennaio || 'Gennaio',
        this.$root.trans.mesi?.febbraio || 'Febbraio',
        this.$root.trans.mesi?.marzo || 'Marzo',
        this.$root.trans.mesi?.aprile || 'Aprile',
        this.$root.trans.mesi?.maggio || 'Maggio',
        this.$root.trans.mesi?.giugno || 'Giugno',
        this.$root.trans.mesi?.luglio || 'Luglio',
        this.$root.trans.mesi?.agosto || 'Agosto',
        this.$root.trans.mesi?.settembre || 'Settembre',
        this.$root.trans.mesi?.ottobre || 'Ottobre',
        this.$root.trans.mesi?.novembre || 'Novembre',
        this.$root.trans.mesi?.dicembre || 'Dicembre'
      ];
      if (dataInizio.getMonth() === dataFine.getMonth() && dataInizio.getFullYear() === dataFine.getFullYear()) {
        return `${nomiMesi[dataInizio.getMonth()]} ${dataInizio.getFullYear()}`;
      }
      if (dataInizio.getFullYear() === dataFine.getFullYear()) {
        return `${nomiMesi[dataInizio.getMonth()]} - ${nomiMesi[dataFine.getMonth()]} ${dataInizio.getFullYear()}`;
      } else {
        return `${nomiMesi[dataInizio.getMonth()]} ${dataInizio.getFullYear()} - ${nomiMesi[dataFine.getMonth()]} ${dataFine.getFullYear()}`;
      }
    },
    navigaIndietro() {
      if (this.loading) return;
      const nuovaDataCentrale = new Date(this.dataCentrale);
      const giorniDaSpostare = this.timingSceltoInterno === 1 ? 7 : 14;
      nuovaDataCentrale.setDate(nuovaDataCentrale.getDate() - giorniDaSpostare);
      this.dataCentrale = nuovaDataCentrale;
      this.calcolaIntervalloData(nuovaDataCentrale);
      this.caricaDati();
    },
    navigaAvanti() {
      if (this.loading) return;
      const nuovaDataCentrale = new Date(this.dataCentrale);
      const giorniDaSpostare = this.timingSceltoInterno === 1 ? 7 : 14;
      nuovaDataCentrale.setDate(nuovaDataCentrale.getDate() + giorniDaSpostare);
      this.dataCentrale = nuovaDataCentrale;
      this.calcolaIntervalloData(nuovaDataCentrale);
      this.caricaDati();
    },
    async caricaDati() {
      if (!this.periodoAttuale.inizio || !this.periodoAttuale.fine) return;
      this.loading = true;
      try {
        const response = await axios.post(route('timing/carica-dati'), {
          data_inizio: this.formatDateForServer(this.periodoAttuale.inizio),
          data_fine: this.formatDateForServer(this.periodoAttuale.fine),
          dipendenti_selezionati: this.dipendentiSelezionati,
          timing_scelto: this.timingSceltoInterno
        });
        this.datigrigliaInterna = response.data.datigriglia;
        this.intestazioniInterne = response.data.intestazioni;
        console.log('Dati caricati con successo');
      } catch (error) {
        console.error('Errore nel caricamento dati:', error);
        this.showToast('error', 'Errore', 'Errore nel caricamento dati');
      } finally {
        this.loading = false;
      }
    },
    formatDateForServer(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatDay(data) {
      let dateString = data;
      if (data && data.giorno) {
        dateString = data.giorno;
      }
      if (typeof dateString === 'string') {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${day}/${month}`;
      }
      return '';
    },
    formatDayName(data) {
      const date = new Date(data);
      const giorni = [
        this.$root.trans.giorni?.domenica || 'Domenica',
        this.$root.trans.giorni?.lunedi || 'Lunedì',
        this.$root.trans.giorni?.martedi || 'Martedì',
        this.$root.trans.giorni?.mercoledi || 'Mercoledì',
        this.$root.trans.giorni?.giovedi || 'Giovedì',
        this.$root.trans.giorni?.venerdi || 'Venerdì',
        this.$root.trans.giorni?.sabato || 'Sabato'
      ];
      return giorni[date.getDay()];
    },
    formatDate(data) {
      const date = new Date(data);
      return date.toLocaleDateString('it-IT');
    },
    getCellClass(dataString, datiGiorno, colore) {
      const date = new Date(dataString);
      const dayOfWeek = date.getDay();
      let classes = ['timing-cell'];
      if (colore === 'orange') classes.push('sabato');
      else if (colore === 'red') classes.push('domenica');
      else if (dayOfWeek === 0) classes.push('domenica');
      else if (dayOfWeek === 6) classes.push('sabato');
      if (datiGiorno) {
        if (datiGiorno.confermato && datiGiorno.confermato > 0) classes.push('confermato');
        if (datiGiorno.straordinari && datiGiorno.straordinari > 0) classes.push('straordinario');
        if (datiGiorno.assenze && datiGiorno.assenze > 0) classes.push('assenza');
        if (datiGiorno.avvisi && datiGiorno.avvisi > 0) classes.push('avviso');
        if (datiGiorno.minuti && datiGiorno.minuti !== '00:00') {
          classes.push('has-ore');
        }
      } else {
        classes.push('empty');
      }
      return classes;
    },
    getStatoClass(confermato) {
      return confermato > 0 ? 'badge badge-success' : 'badge badge-secondary';
    },
    apriDettaglio(dipendente_id, data, dati) {
      const dipendente = this.datigrigliaInterna[dipendente_id];
      this.dettaglioSelezionato = {
        dipendente_id: dipendente_id,
        dipendente: dipendente.cognome_nome,
        data: data,
        dati: dati
      };
      const vm = this;
      axios.post(route('timing/dettaglio'), {
        dipendenti_id: dipendente_id,
        giorno: data
      }).then(function (response) {
        vm.dettaglioTimbrature = response.data;
        console.log('Dettaglio timbrature:', vm.dettaglioTimbrature);
      }).catch(function (error) {
        console.error('Errore nel caricamento dettaglio:', error);
        vm.dettaglioTimbrature = null;
      });
      this.drawerVisible = true;
    },
    formatIntervalloTimbratura(ingresso, uscita) {
      const ingressoDate = new Date(ingresso);
      const uscitaDate = new Date(uscita);
      const pad = v => (v < 10 ? '0' + v : v);
      function formatHHmm(d) {
        return pad(d.getHours()) + ':' + pad(d.getMinutes());
      }
      function formatGGMM_HHmm(d) {
        return pad(d.getDate()) + '-' + pad(d.getMonth() + 1) + ' ' + formatHHmm(d);
      }
      let stessoGiorno = false;
      if (uscita == null || ingresso == null) {
        stessoGiorno = true;
      } else {
        stessoGiorno =
          ingressoDate.getFullYear() === uscitaDate.getFullYear() &&
          ingressoDate.getMonth() === uscitaDate.getMonth() &&
          ingressoDate.getDate() === uscitaDate.getDate();
      }
      if (stessoGiorno) {
        return [formatHHmm(ingressoDate), formatHHmm(uscitaDate)];
      } else {
        return [formatGGMM_HHmm(ingressoDate), formatGGMM_HHmm(uscitaDate)];
      }
    },
    stampa_errore(errore) {
      if (!errore || typeof errore !== 'string') return '';
      const codiciUnici = [...new Set(errore.split('|').filter(Boolean))];
      const messaggiErrore = {
        T001: this.$root.trans.t001,
        T002: this.$root.trans.t002,
        T003: this.$root.trans.t003,
        T004: this.$root.trans.t004,
        T005: this.$root.trans.t005,
        T006: this.$root.trans.t006,
        T007: this.$root.trans.t007,
        T008: this.$root.trans.t008,
        T008u: this.$root.trans.t008u,
        T009: this.$root.trans.t009,
        T010: this.$root.trans.t010,
        T011: this.$root.trans.t011,
        T012: this.$root.trans.t012,
        T013: this.$root.trans.t013,
        T014: this.$root.trans.t014,
      };
      const messaggio = codiciUnici.map(codice => messaggiErrore[codice]);
      return messaggio.join('<br/>');
    },
    icona_errore(errore,tipo) {
      if (!errore || typeof errore !== 'string') return '';
      const codiciUnici = [...new Set(errore.split('|').filter(Boolean))];
      const messaggiErrore_ingresso = {
        T002: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T003: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T004: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T006: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T008: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T009: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T011: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T013: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
      };
      const messaggiErrore_uscita = {
        T001: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T003: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T005: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T007: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T008u: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T010: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T012: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
        T014: '<i class="pi pi-exclamation-triangle" style="color: #FFA726; margin-right: 4px;" />',
      };
      const messaggiErrore = tipo === 1 ? messaggiErrore_ingresso : messaggiErrore_uscita;
      const messaggio = codiciUnici.map(codice => messaggiErrore[codice]);
      return messaggio.join('&nbsp;');
    },
    cerca() {
      console.log('cerca');
      this.caricaDati();
    },
    hasCodiceT003() {
      if (!this.dettaglioTimbrature || this.dettaglioTimbrature.length === 0) return false;
      return this.dettaglioTimbrature.some(timbratura => {
        if (!timbratura.errore || typeof timbratura.errore !== 'string') return false;
        return timbratura.errore.split('|').includes('T003');
      });
    },
    getDettaglioStraordinariConfigurato(dettaglioStraordinari) {
      try {
        const parsed = typeof dettaglioStraordinari === 'string'
          ? JSON.parse(dettaglioStraordinari)
          : dettaglioStraordinari;
        let dettagli = [];
        Object.keys(this.categorieStraordinari).forEach(categoria => {
          if (parsed[categoria] && parsed[categoria].totale_minuti > 0) {
            const ore = Math.floor(parsed[categoria].totale_minuti / 60);
            const minuti = parsed[categoria].totale_minuti % 60;
            const tempo = `${ore.toString().padStart(2, '0')}:${minuti.toString().padStart(2, '0')}`;
            const ore_approvati = Math.floor(parsed[categoria].totale_minuti_approvati / 60);
            const minuti_approvati = parsed[categoria].totale_minuti_approvati % 60;
            const tempo_approvati = `${ore_approvati.toString().padStart(2, '0')}:${minuti_approvati.toString().padStart(2, '0')}`;
            dettagli.push({
              categoria: categoria,
              nome: this.categorieStraordinari[categoria],
              tempo: tempo,
              minuti: parsed[categoria].totale_minuti,
              approvati: tempo_approvati
            });
          }
        });
        return dettagli;
      } catch (error) {
        console.error('Errore nel parsing dettaglio straordinari:', error);
        return [];
      }
    },
    verificaLettura(timbratura, timbratura_cantieri_differenti) {
      this.timbraturaCorrente = timbratura;
      this.verificaTimbrature = true;
      this.timbraturaCantieriDifferenti = timbratura_cantieri_differenti;
    },
    hasErrorCode(errore, codice) {
      if (!errore || typeof errore !== 'string') return false;
      return errore.split('|').filter(Boolean).includes(codice);
    },
    async confermaTimbratura() {
      if (!this.timbraturaCorrente) return;
      this.loadingVerifica = true;
      try {
        const response = await axios.post(route('timing/conferma-timbratura'), {
          ingresso_id: this.timbraturaCorrente.ingresso_id,
          uscita_id: this.timbraturaCorrente.uscita_id,
          data: this.dettaglioSelezionato?.data || null
        });
        if (response.data.success) {
          this.showToast('success', this.$root.trans.success_form, this.$root.trans.timbratura_confermata);
          await this.ricaricaDettaglio();
          this.chiudiVerifica();
        } else {
          this.showToast('error', this.$root.trans.errore, response.data.message || this.$root.trans.errore_generico);
        }
      } catch (error) {
        console.error('Errore nella conferma:', error);
        this.showToast('error', this.$root.trans.errore, this.$root.trans.errore_conferma_timbratura);
      } finally {
        this.loadingVerifica = false;
      }
    },
    async annullaTimbratura() {
      if (!this.timbraturaCorrente) return;
      this.loadingVerifica = true;
      try {
        const response = await axios.post(route('timing/annulla-timbratura'), {
          ingresso_id: this.timbraturaCorrente.ingresso_id,
          uscita_id: this.timbraturaCorrente.uscita_id,
          dipendente_id: this.dettaglioSelezionato?.dipendente_id || null,
          data: this.dettaglioSelezionato?.data || null
        });
        if (response.data.success) {
          this.showToast('success', this.$root.trans.successo, this.$root.trans.timbratura_annullata);
          await this.ricaricaDettaglio();
          this.chiudiVerifica();
        } else {
          this.showToast('error', this.$root.trans.errore, response.data.message || this.$root.trans.errore_generico);
        }
      } catch (error) {
        console.error('Errore nell\'annullamento:', error);
        this.showToast('error', this.$root.trans.errore, this.$root.trans.errore_annulla_timbratura);
      } finally {
        this.loadingVerifica = false;
      }
    },
    async ricaricaDettaglio() {
      if (!this.dettaglioSelezionato) return;
      try {
        let dipendente_id = null;
        for (const [id, dip] of Object.entries(this.datigrigliaInterna)) {
          if (dip.cognome_nome === this.dettaglioSelezionato.dipendente) {
            dipendente_id = id;
            break;
          }
        }
        if (!dipendente_id) return;
        const response = await axios.post(route('timing/dettaglio'), {
          dipendenti_id: dipendente_id,
          giorno: this.dettaglioSelezionato.data
        });
        this.dettaglioTimbrature = response.data;
        await this.caricaDati();
      } catch (error) {
        console.error('Errore nel ricaricamento dettaglio:', error);
      }
    },
    chiudiVerifica() {
      this.verificaTimbrature = false;
      this.timbraturaCorrente = null;
      this.loadingVerifica = false;
    },
    apriConfermaGiornata() {
      this.confermaGiornataDialog = true;
    },
    chiudiConfermaGiornata() {
      this.confermaGiornataDialog = false;
      this.loadingConfermaGiornata = false;
      this.resetSelezioneAssenza();
    },
    minutiMaggioriDiContratto() {
      if (!this.dettaglioSelezionato || !this.dettaglioSelezionato.dati) return false;

      const minutiLavorati = this.parseMinutiFromString(this.dettaglioSelezionato.dati.minuti);
      const minutiContratto = parseFloat(this.dettaglioSelezionato.dati.ore_contratto) * 60;

      return minutiLavorati > minutiContratto;
    },
    parseMinutiFromString(minutiString) {
      if (!minutiString) return 0;
      const parts = minutiString.split(':');
      if (parts.length === 2) {
        return parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
      return 0;
    },
    async handleConferma() {
      this.loadingConfermaGiornata = true;
      try {
        let dipendente_id = this.getDipendenteIdFromName(this.dettaglioSelezionato.dipendente);

        const response = await axios.post(route('timing/conferma-giornata'), {
          dipendente_id: dipendente_id,
          data: this.dettaglioSelezionato.data
        });

        if (response.data.success) {
          this.showToast('success', this.$root.trans.successo, response.data.message || 'Giornata confermata con successo');
          await this.ricaricaDettaglioEDati();
          this.chiudiConfermaGiornata();
          this.drawerVisible = false;
        } else {
          this.showToast('error', this.$root.trans.errore, response.data.message || 'Errore durante la conferma');
        }
      } catch (error) {
        console.error('Errore nella conferma:', error);
        this.showToast('error', this.$root.trans.errore, 'Errore durante la conferma della giornata');
      } finally {
        this.loadingConfermaGiornata = false;
      }
    },
    async handleCompletaIncompleta() {
      this.loadingConfermaGiornata = true;
      try {
        let dipendente_id = this.getDipendenteIdFromName(this.dettaglioSelezionato.dipendente);

        const response = await axios.post(route('timing/completa-incompleta'), {
          dipendente_id: dipendente_id,
          data: this.dettaglioSelezionato.data
        });

        if (response.data.success) {
          this.showToast('success', this.$root.trans.successo, response.data.message || 'Giornata completata come incompleta');
          await this.ricaricaDettaglioEDati();
          this.chiudiConfermaGiornata();
          this.drawerVisible = false;
        } else {
          this.showToast('error', this.$root.trans.errore, response.data.message || 'Errore durante il completamento');
        }
      } catch (error) {
        console.error('Errore nel completamento incompleta:', error);
        this.showToast('error', this.$root.trans.errore, 'Errore durante il completamento della giornata');
      } finally {
        this.loadingConfermaGiornata = false;
      }
    },
    apriSelezioneAssenza() {
      this.assenzaSelezionataDialog = true;
    },
    chiudiSelezioneAssenza() {
      this.assenzaSelezionataDialog = false;
      this.resetSelezioneAssenza();
    },
    resetSelezioneAssenza() {
      this.tipologiaAssenzaSelezionata = null;
      this.assenzaSelezionata = null;
    },
    onTipologiaChange() {
      this.assenzaSelezionata = null;
    },
    getAssenzePerTipologia() {
      if (!this.tipologiaAssenzaSelezionata) return [];
      return this.assenze.filter(a => a.tipologia_id === this.tipologiaAssenzaSelezionata);
    },
    async handleCompletaAssenza() {
      if (!this.assenzaSelezionata) {
        this.showToast('warning', 'Attenzione', 'Seleziona un\'assenza');
        return;
      }

      this.loadingConfermaGiornata = true;
      try {
        let dipendente_id = this.getDipendenteIdFromName(this.dettaglioSelezionato.dipendente);

        const response = await axios.post(route('timing/completa-con-assenza'), {
          dipendente_id: dipendente_id,
          data: this.dettaglioSelezionato.data,
          tipologia_assenza_id: this.tipologiaAssenzaSelezionata,
          assenza_id: this.assenzaSelezionata
        });

        if (response.data.success) {
          this.showToast('success', this.$root.trans.successo, response.data.message || 'Giornata completata con assenza');
          await this.ricaricaDettaglioEDati();
          this.chiudiSelezioneAssenza();
          this.chiudiConfermaGiornata();
          this.drawerVisible = false;
        } else {
          this.showToast('error', this.$root.trans.errore, response.data.message || 'Errore durante il completamento');
        }
      } catch (error) {
        console.error('Errore nel completamento con assenza:', error);
        this.showToast('error', this.$root.trans.errore, 'Errore durante il completamento con assenza');
      } finally {
        this.loadingConfermaGiornata = false;
      }
    },
    async handleCompensa() {
      this.loadingConfermaGiornata = true;
      try {
        let dipendente_id = this.getDipendenteIdFromName(this.dettaglioSelezionato.dipendente);

        const response = await axios.post(route('timing/compensa-giornata'), {
          dipendente_id: dipendente_id,
          data: this.dettaglioSelezionato.data
        });

        if (response.data.success) {
          if (response.data.compensato_totalmente) {
            this.showToast('success', this.$root.trans.successo, 'Giornata compensata totalmente');
            await this.ricaricaDettaglioEDati();
            this.chiudiConfermaGiornata();
            this.drawerVisible = false;
          } else {
            this.oreDaCompensare = response.data.ore_rimanenti || 0;
            this.messaggioCompensazione = response.data.message || 'Compensazione parziale. Scegli come completare le ore rimanenti.';
            this.showToast('info', 'Info', this.messaggioCompensazione);
          }
        } else {
          this.showToast('error', this.$root.trans.errore, response.data.message || 'Errore durante la compensazione');
        }
      } catch (error) {
        console.error('Errore nella compensazione:', error);
        this.showToast('error', this.$root.trans.errore, 'Errore durante la compensazione');
      } finally {
        this.loadingConfermaGiornata = false;
      }
    },
    getDipendenteIdFromName(nomeDipendente) {
      for (const [id, dip] of Object.entries(this.datigrigliaInterna)) {
        if (dip.cognome_nome === nomeDipendente) {
          return id;
        }
      }
      return null;
    },
    async ricaricaDettaglioEDati() {
      await this.ricaricaDettaglio();
      await this.caricaDati();
    },
    async verificaTutteTimbrature(dettaglioSelezione) {
      console.log(dettaglioSelezione);
      if (!dettaglioSelezione) return;
      this.loadingVerifica = true;
      try {
        const response = await axios.post(route('timing/conferma-tutte-timbrature'), {
          dipendenti_id: dettaglioSelezione.dipendente_id,
          giorno: dettaglioSelezione.data,
        });
        if (response.data.success) {
          this.showToast('success', this.$root.trans.success_form, this.$root.trans.timbratura_confermata);
          await this.ricaricaDettaglio();
        } else {
          this.showToast('error', this.$root.trans.errore, response.data.message || this.$root.trans.errore_generico);
        }
      } catch (error) {
        console.error('Errore nella conferma:', error);
        this.showToast('error', this.$root.trans.errore, this.$root.trans.errore_conferma_timbratura);
      } finally {
        this.loadingVerifica = false;
      }

    },
    async assegnaCliente() {
      if (!this.timbraturaCorrente) return;

      this.loadingQrcode = true;
      try {
        const response = await axios.post(route('timing/assegna-cliente'), {
          ingresso_id: this.timbraturaCorrente.ingresso_id,
          uscita_id: this.timbraturaCorrente.uscita_id
        });

        console.log('Response completa:', response.data); // Debug

        if (response.data && response.data.dati) {
          // Nota: c'è un typo nel backend "diveri" invece di "diversi"
          this.timbraturaCanteriDiversi = response.data.dati.timbratura_cantieri_diversi || 0;
          this.qrcodeList = response.data.dati.qrcode || [];

          console.log('Cantieri diversi:', this.timbraturaCanteriDiversi); // Debug
          console.log('QRCode list:', this.qrcodeList); // Debug

          // Reset selezioni
          this.qrcodeSelezionato = null;
          this.qrcodeIngressoSelezionato = null;
          this.qrcodeUscitaSelezionato = null;

          // Apri il dialog
          this.qrcodeDialog = true;
        }
      } catch (error) {
        console.error('Errore nel caricamento QR Code:', error);
        this.showToast('error', this.$root.trans.errore, 'Errore nel caricamento dei QR Code');
      } finally {
        this.loadingQrcode = false;
      }
    },
    async salvaEVerifica() {
      if (!this.timbraturaCorrente) return;

      // Validazione
      if (this.timbraturaCanteriDiversi === 0) {
        if (!this.qrcodeSelezionato) {
          this.showToast('warning', this.$root.trans.seleziona_cliente);
          return;
        }
      } else {
        if (!this.qrcodeIngressoSelezionato || !this.qrcodeUscitaSelezionato) {
          this.showToast('warning', this.$root.trans.seleziona_cliente);
          return;
        }
      }

      this.loadingQrcode = true;
      try {
        const response = await axios.post(route('timing/salva-assegna-cliente'), {
          id_ingresso: this.timbraturaCorrente.ingresso_id,
          id_uscita: this.timbraturaCorrente.uscita_id,
          select1: this.timbraturaCanteriDiversi === 0 ? this.qrcodeSelezionato : this.qrcodeIngressoSelezionato,
          select2: this.timbraturaCanteriDiversi === 0 ? this.qrcodeSelezionato : this.qrcodeUscitaSelezionato
        });

        if (response.data.success) {
          this.showToast('success', this.$root.trans.successo, response.data.message || 'QR Code assegnato con successo');
          await this.ricaricaDettaglio();
          this.chiudiQrcodeDialog();
          this.chiudiVerifica();
        } else {
          this.showToast('error', this.$root.trans.errore, response.data.message || 'Errore durante il salvataggio');
        }
      } catch (error) {
        console.error('Errore nel salvataggio QR Code:', error);
        this.showToast('error', this.$root.trans.errore, 'Errore durante il salvataggio del QR Code');
      } finally {
        this.loadingQrcode = false;
      }
    },
    chiudiQrcodeDialog() {
      this.qrcodeDialog = false;
      this.qrcodeList = [];
      this.qrcodeSelezionato = null;
      this.qrcodeIngressoSelezionato = null;
      this.qrcodeUscitaSelezionato = null;
      this.timbraturaCanteriDiversi = 0;
    },
    async assegnaStessoCliente() {
      if (!this.timbraturaCorrente) return;

      this.loadingQrcode = true;
      try {
        const response = await axios.post(route('timing/assegna-stesso-cliente'), {
          ingresso_id: this.timbraturaCorrente.ingresso_id,
          uscita_id: this.timbraturaCorrente.uscita_id
        });

        console.log('Response completa:', response.data); // Debug

        if (response.data && response.data.dati) {
          // Nota: c'è un typo nel backend "diveri" invece di "diversi"
          await this.ricaricaDettaglio();
          this.chiudiQrcodeDialog();
          this.chiudiVerifica();
        }
      } catch (error) {
        console.error('Errore nel caricamento QR Code:', error);
        this.showToast('error', this.$root.trans.errore, 'Errore nel caricamento dei QR Code');
      } finally {
        this.loadingQrcode = false;
      }
    },

    // Metodi per modifica timbrature
    async apriModificaTimbrature() {
      /*if (!this.dettaglioTimbrature || this.dettaglioTimbrature.length === 0) {
        this.showToast('warning', 'Attenzione', 'Nessuna timbratura da modificare');
        return;
      }*/

      // Carica lista clienti se non già caricata
      if (this.qrcodeList.length === 0) {
        try {
          const response = await axios.post(route('timing/assegna-cliente'), {
            ingresso_id: this.dettaglioTimbrature[0]?.ingresso_id || 0,
            uscita_id: this.dettaglioTimbrature[0]?.uscita_id || 0
          });
          if (response.data && response.data.dati) {
            this.qrcodeList = response.data.dati.qrcode || [];
          }
        } catch (error) {
          console.error('Errore nel caricamento clienti:', error);
        }
      }

      // Costruisci la griglia delle timbrature
      this.timbraturaGriglia = this.dettaglioTimbrature.map((t, index) => ({
        id: `riga_${index}`,
        posizioneOriginale: index,
        eliminata: false,
        ingresso: t.ingresso_id > 0 ? {
          id: t.ingresso_id,
          dataora: t.ingresso_dataora,
          cliente: t.cliente_ingresso,
          qrId: t.qringresso_id || null,
          indirizzo: t.ingresso_indirizzo,
          coordinate: t.ingresso_coordinate,
          qrcode: t.ingresso_qrcode,
          errore: t.errore,
          isNew: false,
          isModified: false,
          isDeleted: (t.stato_ingresso || 0) < 0,
          dataOraOriginale: t.ingresso_dataora,
          qrIdOriginale: t.qringresso_id || null,
          stato: t.stato_ingresso || 0,
          rigaOriginale: index,
          tipoOriginale: 'ingresso'
        } : null,
        uscita: t.uscita_id > 0 ? {
          id: t.uscita_id,
          dataora: t.uscita_dataora,
          cliente: t.cliente_uscita,
          qrId: t.qruscita_id || null,
          indirizzo: t.uscita_indirizzo,
          coordinate: t.uscita_coordinate,
          qrcode: t.uscita_qrcode,
          errore: t.errore,
          isNew: false,
          isModified: false,
          isDeleted: (t.stato_uscita || 0) < 0,
          dataOraOriginale: t.uscita_dataora,
          qrIdOriginale: t.qruscita_id || null,
          stato: t.stato_uscita || 0,
          rigaOriginale: index,
          tipoOriginale: 'uscita'
        } : null
      }));

      this.timbraturaGrigliaOriginale = JSON.parse(JSON.stringify(this.timbraturaGriglia));

      this.modificaTimbratureDialog = true;
    },

    chiudiModificaTimbrature() {
      this.modificaTimbratureDialog = false;
      this.timbraturaGriglia = [];
      this.draggedItem = null;
      this.dragOverCell = null;
    },

    aggiungiRigaTimbratura() {
      this.timbraturaGriglia.push({
        id: `nuova_riga_${Math.abs(this.contatoreTempId--)}`,
        posizioneOriginale: null, // Nuova riga
        eliminata: false,
        ingresso: null,
        uscita: null
      });
    },

    rimuoviRiga(index) {
      if (this.timbraturaGriglia.length === 1) {
        this.showToast('warning', 'Attenzione', 'Deve rimanere almeno una riga');
        return;
      }

      // Controlla se la riga ha timbrature esistenti (non nuove)
      const riga = this.timbraturaGriglia[index];
      const haTimbratureEsistenti = (riga.ingresso && !riga.ingresso.isNew) ||
        (riga.uscita && !riga.uscita.isNew);

      if (haTimbratureEsistenti) {
        // Se ha timbrature esistenti, usa il dialog di conferma
        this.confermaEliminazioneRiga(index);
      } else {
        // Se ha solo timbrature nuove o è vuota, rimuovi direttamente
        this.timbraturaGriglia.splice(index, 1);
        this.showToast('success', 'Successo', 'Riga rimossa');
      }
    },

    rimuoviTimbratura(rigaIndex, tipo) {
      if (confirm('Sei sicuro di voler rimuovere questa timbratura?')) {
        this.timbraturaGriglia[rigaIndex][tipo] = null;
      }
    },

    onDragStart(event, rigaIndex, tipo, timbratura) {
      this.draggedItem = {
        rigaIndex,
        tipo,
        timbratura
      };
      event.dataTransfer.effectAllowed = 'move';
      event.target.classList.add('dragging');
    },

    onDragEnd(event) {
      event.target.classList.remove('dragging');
      this.draggedItem = null;
      this.dragOverCell = null;
    },

    onDragOver(event, rigaIndex, tipo) {
      event.preventDefault();
      this.dragOverCell = `${rigaIndex}-${tipo}`;
    },

    onDragLeave() {
      this.dragOverCell = null;
    },

    onDrop(event, rigaIndex, tipo) {
      event.preventDefault();
      this.dragOverCell = null;

      if (!this.draggedItem) return;

      // Se si trascina sulla stessa cella, non fare nulla
      if (this.draggedItem.rigaIndex === rigaIndex && this.draggedItem.tipo === tipo) {
        return;
      }

      // Salva la timbratura di destinazione se esiste
      const destinazione = this.timbraturaGriglia[rigaIndex][tipo];

      // IMPORTANTE: Crea una copia dell'oggetto timbratura per non perdere i riferimenti
      const timbraturaTrasinata = { ...this.draggedItem.timbratura };

      // Sposta la timbratura trascinata nella nuova posizione
      this.timbraturaGriglia[rigaIndex][tipo] = timbraturaTrasinata;

      // Se c'era già una timbratura nella destinazione, mettila nella posizione originale
      if (destinazione) {
        this.timbraturaGriglia[this.draggedItem.rigaIndex][this.draggedItem.tipo] = destinazione;
      } else {
        // Altrimenti svuota la cella originale
        this.timbraturaGriglia[this.draggedItem.rigaIndex][this.draggedItem.tipo] = null;
      }

      this.showToast('success', 'Successo', 'Timbratura spostata');

      // Debug
      console.log('Timbratura spostata:', {
        da: { riga: this.draggedItem.rigaIndex, tipo: this.draggedItem.tipo },
        a: { riga: rigaIndex, tipo: tipo },
        rigaOriginale: timbraturaTrasinata.rigaOriginale,
        tipoOriginale: timbraturaTrasinata.tipoOriginale
      });
    },

    formatOrario(dataora) {
      if (!dataora) return '';
      const date = new Date(dataora);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    },

    apriInserimentoTimbratura(rigaIndex, tipo) {
      this.rigaInserimento = rigaIndex;
      this.tipoInserimento = tipo;
      this.nuovaTimbratura = {
        dataora: null,
        cliente_id: null,
        indirizzo: '',
        coordinate: ''
      };
      this.inserimentoTimbrataDialog = true;
    },

    chiudiInserimentoTimbratura() {
      this.inserimentoTimbrataDialog = false;
      this.rigaInserimento = null;
      this.tipoInserimento = null;
      this.nuovaTimbratura = {
        dataora: null,
        cliente_id: null,
        indirizzo: '',
        coordinate: ''
      };
    },

    confermaInserimentoTimbratura() {
      if (!this.nuovaTimbratura.dataora) {
        this.showToast('warning', 'Attenzione', 'Inserisci data e ora');
        return;
      }

      // Trova il cliente selezionato
      const clienteSelezionato = this.qrcodeList.find(c => c.id === this.nuovaTimbratura.cliente_id);

      // Crea la nuova timbratura
      const nuovaTimbr = {
        id: this.contatoreTempId--,
        dataora: this.nuovaTimbratura.dataora,
        cliente: clienteSelezionato ? clienteSelezionato.nome : '',
        cliente_id: this.nuovaTimbratura.cliente_id,
        indirizzo: this.nuovaTimbratura.indirizzo,
        coordinate: this.nuovaTimbratura.coordinate,
        qrcode: clienteSelezionato ? clienteSelezionato.qrcode : null,
        isNew: true,
        isModified: false,
        dataOraOriginale: null,
        clienteIdOriginale: null,
        rigaOriginale: null,
        tipoOriginale: null
      };

      // Inserisci nella griglia
      this.timbraturaGriglia[this.rigaInserimento][this.tipoInserimento] = nuovaTimbr;

      this.showToast('success', 'Successo', 'Timbratura aggiunta');
      this.chiudiInserimentoTimbratura();
    },
    /**
     * NUOVO: Apre il dialog per modificare una singola timbratura
     */
    async apriModificaSingolaTimbratura(rigaIndex, tipo) {
      const riga = this.timbraturaGriglia[rigaIndex];
      const timbratura = riga[tipo];
      console.log(timbratura);

      // Carica lista clienti se necessario
      if (this.qrcodeList.length === 0) {
        await this.caricaListaClienti();
      }

      this.rigaInModifica = rigaIndex;
      this.tipoModificaSingola = tipo;
      this.timbraturaInModifica = { ...timbratura };
      let clienteId = timbratura.qrId;
      if (timbratura.qrId) {
        clienteId = timbratura.qrId;
        console.log('Debug - Trovato cliente_id diretto:', clienteId);
      } else if (timbratura.cliente && timbratura.cliente !== 'EXTRA' && timbratura.cliente.trim() !== '') {
        const clienteTrovato = this.qrcodeList.find(c => {
          // Prova match esatto (case insensitive)
          const nomeCliente = timbratura.cliente.toLowerCase().trim();
          const nomeQR = c.nome.toLowerCase().trim();
          return nomeQR === nomeCliente;
        });

        if (clienteTrovato) {
          clienteId = clienteTrovato.id;
          console.log('Debug - Trovato cliente per nome:', clienteTrovato);
        } else {
          // Prova match parziale se non trova match esatto
          const clienteTrovatoParziale = this.qrcodeList.find(c => {
            const nomeCliente = timbratura.cliente.toLowerCase().trim();
            const nomeQR = c.nome.toLowerCase().trim();
            return nomeQR.includes(nomeCliente) || nomeCliente.includes(nomeQR);
          });

          if (clienteTrovatoParziale) {
            clienteId = clienteTrovatoParziale.id;
            console.log('Debug - Trovato cliente con match parziale:', clienteTrovatoParziale);
          }
        }
      }

      console.log('Debug - Cliente ID finale:', clienteId);

      // Inizializza i dati per la modifica
      this.timbraturaModificata = {
        dataora: timbratura.dataora ? new Date(timbratura.dataora) : null,
        cliente_id: clienteId,
      };

      console.log('Debug - Dati modifica inizializzati:', this.timbraturaModificata);

      this.modificaSingolaDialog = true;

      // Verifica che il cliente sia effettivamente selezionato dopo l'apertura del dialog
      this.$nextTick(() => {
        console.log('Debug - Stato finale dropdown cliente:', this.timbraturaModificata.cliente_id);
      });
    },

    /**
     * NUOVO: Chiude il dialog di modifica singola
     */
    chiudiModificaSingola() {
      this.modificaSingolaDialog = false;
      this.timbraturaInModifica = null;
      this.rigaInModifica = null;
      this.tipoModificaSingola = null;
      this.timbraturaModificata = {
        dataora: null,
        cliente_id: null,
      };
      this.loadingModificaSingola = false;
    },

    /**
     * NUOVO: Salva le modifiche alla singola timbratura
     */
    async salvaModificaSingola() {
      if (!this.timbraturaModificata.dataora) {
        this.showToast('warning', 'Attenzione', 'Inserisci data e ora');
        return;
      }

      if (this.rigaInModifica === null || !this.tipoModificaSingola) {
        this.showToast('error', 'Errore', 'Dati di modifica non validi');
        return;
      }

      // === VALIDAZIONE LOGICA DELLE DATE ===
      const rigaCorrente = this.timbraturaGriglia[this.rigaInModifica];
      const nuovaDataOra = new Date(this.timbraturaModificata.dataora);

      if (this.tipoModificaSingola === 'ingresso') {
        // Se sto modificando l'ingresso, controllo che l'uscita sia successiva
        if (rigaCorrente.uscita && rigaCorrente.uscita.dataora) {
          const dataOraUscita = new Date(rigaCorrente.uscita.dataora);

          if (nuovaDataOra >= dataOraUscita) {
            this.showToast('warning', 'Attenzione',
              'L\'orario di ingresso deve essere precedente all\'orario di uscita della stessa riga');
            return;
          }
        }
      } else if (this.tipoModificaSingola === 'uscita') {
        // Se sto modificando l'uscita, controllo che l'ingresso sia precedente
        if (rigaCorrente.ingresso && rigaCorrente.ingresso.dataora) {
          const dataOraIngresso = new Date(rigaCorrente.ingresso.dataora);

          if (nuovaDataOra <= dataOraIngresso) {
            this.showToast('warning', 'Attenzione',
              'L\'orario di uscita deve essere successivo all\'orario di ingresso della stessa riga');
            return;
          }
        }
      }
      // === FINE VALIDAZIONE ===


      this.loadingModificaSingola = true;

      try {
        // Trova il cliente selezionato
        const clienteSelezionato = this.qrcodeList.find(c => c.id === this.timbraturaModificata.cliente_id);
        const timbraturaCorrente = this.timbraturaGriglia[this.rigaInModifica][this.tipoModificaSingola];

        // Aggiorna la timbratura nella griglia
        const timbraturaAggiornata = {
          ...timbraturaCorrente,
          dataora: this.timbraturaModificata.dataora,
          cliente_id: this.timbraturaModificata.cliente_id,
          cliente: clienteSelezionato ? clienteSelezionato.nome : '',
          qrcode: clienteSelezionato ? clienteSelezionato.qrcode : null,
          isModified: !timbraturaCorrente.isNew // Marca come modificata solo se non è nuova
        };

        // Aggiorna la griglia
        this.timbraturaGriglia[this.rigaInModifica][this.tipoModificaSingola] = timbraturaAggiornata;

        this.showToast('success', 'Successo', 'Timbratura modificata con successo');
        this.chiudiModificaSingola();

      } catch (error) {
        console.error('Errore nella modifica singola:', error);
        this.showToast('error', 'Errore', 'Errore durante la modifica della timbratura');
      } finally {
        this.loadingModificaSingola = false;
      }
    },

    /**
     * NUOVO: Apre il dialog di conferma per eliminare una riga
     */
    confermaEliminazioneRiga(rigaIndex) {
      if (this.timbraturaGriglia.length === 1) {
        this.showToast('warning', 'Attenzione', 'Non puoi eliminare l\'ultima riga');
        return;
      }

      this.rigaDaEliminare = rigaIndex;
      this.confermaEliminazioneDialog = true;
    },

    /**
     * NUOVO: Annulla l'eliminazione della riga
     */
    annullaEliminazioneRiga() {
      this.confermaEliminazioneDialog = false;
      this.rigaDaEliminare = null;
    },

    /**
     * NUOVO: Conferma ed esegue l'eliminazione della riga
     */
    confermaEliminazioneDefinitiva() {
      if (this.rigaDaEliminare !== null) {
        const riga = this.timbraturaGriglia[this.rigaDaEliminare];

        // Controlla se la riga ha timbrature esistenti (non nuove)
        const haTimbratureEsistenti = (riga.ingresso && !riga.ingresso.isNew) ||
          (riga.uscita && !riga.uscita.isNew);

        if (haTimbratureEsistenti) {
          // Per timbrature esistenti: marca come eliminata invece di rimuovere
          riga.eliminata = true;
          //this.showToast('success', 'Successo', 'Ricordati di salvare per completare l\'eliminazione');
        } else {
          // Per timbrature nuove: rimuovi direttamente
          this.timbraturaGriglia.splice(this.rigaDaEliminare, 1);
          //this.showToast('success', 'Successo', 'Coppia timbratura eliminata');
        }
      }
      this.annullaEliminazioneRiga();
    },

    ripristinaRiga(index) {
      const riga = this.timbraturaGriglia[index];
      if (riga.eliminata) {
        riga.eliminata = false;
        return;
      }
      let ripristinata = false;

      if (riga.ingresso && (riga.ingresso.isDeleted || riga.ingresso.stato < 0)) {
        riga.ingresso.isDeleted = false;
        ripristinata = true;
      }

      if (riga.uscita && (riga.uscita.isDeleted || riga.uscita.stato < 0)) {
        riga.uscita.isDeleted = false;
        ripristinata = true;
      }

      if (ripristinata) {
        this.showToast('success', 'Successo', 'Timbrature ripristinate');
      }
    },
    hasTimbratureEliminate(riga) {
      return (riga.ingresso && (riga.ingresso.isDeleted || riga.ingresso.stato < 0)) ||
        (riga.uscita && (riga.uscita.isDeleted || riga.uscita.stato < 0));
    },

    hasTimbratureAttive(riga) {
      return (riga.ingresso && !riga.ingresso.isDeleted && riga.ingresso.stato >= 0) ||
        (riga.uscita && !riga.uscita.isDeleted && riga.uscita.stato >= 0);
    },

    /**
     * NUOVO: Verifica se una riga ha modifiche
     */
    rigaHaModifiche(riga) {
      if (riga.eliminata) return true;
      if (riga.posizioneOriginale !== null && riga.posizioneOriginale !== this.timbraturaGriglia.indexOf(riga)) return true;

      // Controlla se ci sono modifiche negli stati delle timbrature
      const ingressoModificato = riga.ingresso && (
        riga.ingresso.isModified ||
        riga.ingresso.isNew ||
        (riga.ingresso.isDeleted !== (riga.ingresso.stato < 0))
      );

      const uscitaModificata = riga.uscita && (
        riga.uscita.isModified ||
        riga.uscita.isNew ||
        (riga.uscita.isDeleted !== (riga.uscita.stato < 0))
      );

      return ingressoModificato || uscitaModificata;
    },
    toggleEliminazioneTimbratura(rigaIndex, tipo) {
      const timbratura = this.timbraturaGriglia[rigaIndex][tipo];
      if (!timbratura) return;

      if (timbratura.isNew) {
        // Se è una timbratura nuova, rimuovila completamente
        this.timbraturaGriglia[rigaIndex][tipo] = null;
        //this.showToast('success', 'Successo', 'Timbratura rimossa');
      } else {
        // Se è una timbratura esistente, cambia lo stato
        timbratura.isDeleted = !timbratura.isDeleted;
        const azione = timbratura.isDeleted ? 'eliminata' : 'ripristinata';
        //this.showToast('success', 'Successo', `Timbratura ${azione}`);
      }
    },

    /**
     * NUOVO: Carica la lista dei clienti/QR code se non già presente
     */
    async caricaListaClienti() {
      if (this.qrcodeList.length > 0) return;

      try {
        // Usa una timbratura esistente per il caricamento, o valori di default
        const response = await axios.post(route('timing/assegna-cliente'), {
          ingresso_id: this.dettaglioTimbrature[0]?.ingresso_id || 0,
          uscita_id: this.dettaglioTimbrature[0]?.uscita_id || 0
        });

        if (response.data && response.data.dati) {
          this.qrcodeList = response.data.dati.qrcode || [];
        }
      } catch (error) {
        console.error('Errore nel caricamento clienti:', error);
        this.showToast('error', 'Errore', 'Impossibile caricare la lista clienti');
      }
    },
    async salvaModificheTimbrature() {
      // Validazione: ogni riga deve avere almeno ingresso o uscita
      const righeNonEliminate = this.timbraturaGriglia.filter(r => !r.eliminata);
      const righeVuote = righeNonEliminate.filter(r => !r.ingresso && !r.uscita);
      if (righeVuote.length > 0) {
        this.showToast('warning', 'Attenzione', 'Rimuovi le righe vuote prima di salvare');
        return;
      }
      const righeIncomplete = righeNonEliminate.filter(r => {
        const haIngresso = r.ingresso && !r.ingresso.isDeleted;
        const haUscita = r.uscita && !r.uscita.isDeleted;
        return (haIngresso && !haUscita) || (!haIngresso && haUscita);
      });

      /*if (righeIncomplete.length > 0) {
        this.showToast('warning', 'Attenzione',
          'Ogni riga deve avere sia ingresso che uscita. Completa le righe mancanti o eliminale.');
        return;
      }*/

      this.loadingSalvaTimbrature = true;

      try {
        const dipendente_id = this.getDipendenteIdFromName(this.dettaglioSelezionato.dipendente);
        const modifiche = this.analizzaModifiche();

        // Prepara i dati da inviare con tutte le informazioni di modifica
        const datiDaInviare = {
          dipendente_id: dipendente_id,
          data: this.dettaglioSelezionato.data,
          modifiche: modifiche,
          grigliaCompleta: this.timbraturaGriglia.map((riga, index) => ({
            id: riga.id,
            posizione: index,
            eliminata: riga.eliminata,
            posizioneOriginale: riga.posizioneOriginale,
            ingresso: riga.ingresso ? {
              id: riga.ingresso.id,
              dataora: riga.ingresso.dataora,
              cliente_id: riga.ingresso.cliente_id,
              isNew: riga.ingresso.isNew,
              isModified: riga.ingresso.isModified,
              isDeleted: riga.ingresso.isDeleted || false,
              statoOriginale: riga.ingresso.stato || 0,
              rigaOriginale: riga.ingresso.rigaOriginale,
              tipoOriginale: riga.ingresso.tipoOriginale
            } : null,
            uscita: riga.uscita ? {
              id: riga.uscita.id,
              dataora: riga.uscita.dataora,
              cliente_id: riga.uscita.cliente_id,
              isNew: riga.uscita.isNew,
              isModified: riga.uscita.isModified,
              isDeleted: riga.uscita.isDeleted || false,
              statoOriginale: riga.uscita.stato || 0,
              rigaOriginale: riga.uscita.rigaOriginale,
              tipoOriginale: riga.uscita.tipoOriginale
            } : null
          }))
        };

        console.log('Dati da inviare:', datiDaInviare);

        const response = await axios.post(route('timing/richiesta-modifiche-timbrature-griglia'), datiDaInviare);

        if (response.data.success) {
          this.showToast('success', this.$root.trans.successo, response.data.message || 'Timbrature aggiornate con successo');
          await this.ricaricaDettaglioEDati();
          this.chiudiModificaTimbrature();
        } else {
          this.showToast('error', this.$root.trans.errore, response.data.message || 'Errore durante il salvataggio');
        }

      } catch (error) {
        console.error('Errore nel salvataggio:', error);
        this.showToast('error', this.$root.trans.errore, 'Errore durante il salvataggio delle timbrature');
      } finally {
        this.loadingSalvaTimbrature = false;
      }
    },
    analizzaModifiche() {
      const modifiche = {
        eliminate: [],
        modificate: [],
        nuove: [],
        spostate: [],
        eliminateSingole: []
      };

      this.timbraturaGriglia.forEach((riga, indiceCorrente) => {
        // Righe eliminate
        if (riga.eliminata) {
          modifiche.eliminate.push({
            id: riga.id,
            posizioneOriginale: riga.posizioneOriginale,
            ingresso_id: riga.ingresso?.id || null,
            uscita_id: riga.uscita?.id || null
          });
          return;
        }

        // Righe spostate (se la posizione originale è diversa dall'attuale)
        if (riga.posizioneOriginale !== null && riga.posizioneOriginale !== indiceCorrente) {
          modifiche.spostate.push({
            id: riga.id,
            posizioneOriginale: riga.posizioneOriginale,
            posizioneCorrente: indiceCorrente,
            ingresso_id: riga.ingresso?.id || null,
            uscita_id: riga.uscita?.id || null
          });
        }

        // Timbrature modificate o nuove
        ['ingresso', 'uscita'].forEach(tipo => {
          const timbratura = riga[tipo];
          if (!timbratura) return;

          if (!timbratura.isNew &&
            timbratura.rigaOriginale !== undefined &&
            (timbratura.rigaOriginale !== indiceCorrente || timbratura.tipoOriginale !== tipo)) {
            modifiche.spostate.push({
              id: timbratura.id,
              tipo: tipo,
              tipoOriginale: timbratura.tipoOriginale,
              rigaOriginale: timbratura.rigaOriginale,
              rigaCorrente: indiceCorrente,
              tipoCorrente: tipo,
              dataora: timbratura.dataora,
              cliente_id: timbratura.cliente_id
            });
          }

          if (timbratura.isNew) {
            modifiche.nuove.push({
              tipo: tipo,
              rigaId: riga.id,
              posizione: indiceCorrente,
              dataora: timbratura.dataora,
              cliente_id: timbratura.cliente_id,
              indirizzo: timbratura.indirizzo,
              coordinate: timbratura.coordinate,
              qrId: timbratura.qrId || ''
            });
          } else if (timbratura.isDeleted && (timbratura.stato || 0) >= 0) {
            modifiche.eliminateSingole.push({
              id: timbratura.id,
              tipo: tipo,
              rigaId: riga.id,
              posizione: indiceCorrente
            });
          } else if (!timbratura.isDeleted && (timbratura.stato || 0) < 0) {
            modifiche.modificate.push({
              id: timbratura.id,
              tipo: tipo,
              rigaId: riga.id,
              posizione: indiceCorrente,
              dataora: timbratura.dataora,
              cliente_id: timbratura.cliente_id,
              dataOraOriginale: timbratura.dataOraOriginale,
              clienteIdOriginale: timbratura.clienteIdOriginale,
              ripristinata: true
            });
          } else if (timbratura.isModified) {
            modifiche.modificate.push({
              id: timbratura.id,
              tipo: tipo,
              rigaId: riga.id,
              posizione: indiceCorrente,
              dataora: timbratura.dataora,
              cliente_id: timbratura.cliente_id,
              dataOraOriginale: timbratura.dataOraOriginale,
              clienteIdOriginale: timbratura.clienteIdOriginale,
              qrId: timbratura.qrId || ''
            });
          }
        });
      });

      return modifiche;
    }
  }
}
</script>

<style scoped>
.timing-header {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.date-navigator {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.5rem;
  background: white;
  align-items: center;
}

.periodo-display {
  min-width: 200px;
  text-align: center;
  padding: 0.5rem 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.mese-anno {
  font-weight: 600;
  color: #495057;
  font-size: 0.95rem;
}

.date-picker-container {
  position: relative;
}

.visualizzazione-selector {
  min-width: 120px;
}

.p-dropdown-sm {
  font-size: 0.875rem;
}

.p-dropdown-sm .p-dropdown-label {
  padding: 0.5rem 0.75rem;
}

.interval-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: white;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 250px;
}

.interval-display:hover,
.interval-display.active {
  border-color: #007bff;
  background-color: #f8f9ff;
}

.interval-text {
  font-weight: 500;
  color: #495057;
}

.calendar-icon {
  color: #6c757d;
  font-size: 1rem;
}

.date-picker-wrapper {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 1rem;
  margin-top: 0.25rem;
}

.date-picker-overlay {
  border: none !important;
}

.date-picker-overlay-bg {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  background: transparent;
}

.p-button-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.p-button-secondary:hover {
  background-color: #545b62;
  border-color: #4e555b;
}

.p-button-success {
  background-color: #28a745;
  border-color: #28a745;
}

.p-button-success:disabled {
  background-color: #28a745;
  border-color: #28a745;
  opacity: 0.8;
}

.p-button {
  transition: all 0.3s ease;
}

.alert-box {
  padding: 0.75rem 1rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.alert-box.warning {
  background-color: #FFF3E0;
  border: 1px solid #FFB74D;
  color: #E65100;
}

.alert-box i {
  font-size: 1.1rem;
}

.conferma-giornata-content .info-giornata {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.conferma-giornata-content .dettaglio-riepilogo {
  background: white;
  padding: 0.75rem;
  border-radius: 4px;
}

.conferma-giornata-content .info-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
}

.conferma-giornata-content .info-row:last-child {
  border-bottom: none;
}

.p-datepicker {
  box-shadow: none !important;
  border: none !important;
}

.p-datepicker-header {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.p-datepicker-calendar td > span {
  border-radius: 50%;
  transition: all 0.2s ease;
}

.p-datepicker-calendar td > span:hover {
  background: #007bff;
  color: white;
}

/* Stili per modifica timbrature */
.modifica-timbrature-content {
  padding: 1rem 0;
}

.timbrature-grid-container {
  overflow-x: auto;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: white;
}

.timbrature-grid {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

.timbrature-grid thead th {
  background: #f8f9fa;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
  color: #495057;
}

.timbrature-grid tbody tr {
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
}

.timbrature-grid tbody tr:hover {
  background-color: #f8f9fa;
}

.timbrature-grid tbody tr:last-child {
  border-bottom: none;
}

.timbrature-grid td {
  padding: 0.75rem 1rem;
  vertical-align: top;
}

.row-number {
  text-align: center;
  font-weight: 600;
  color: #6c757d;
  background: #f8f9fa;
}

.timbratura-cell {
  min-height: 100px;
  position: relative;
  transition: all 0.3s ease;
  border-left: 1px solid #e9ecef;
}

.timbratura-cell.has-data {
  background: #f8f9ff;
}

.timbratura-cell.drag-over {
  background: #e3f2fd;
  border: 2px dashed #2196F3;
  transform: scale(1.02);
}

.timbratura-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: move;
  transition: all 0.2s ease;
  gap: 0.5rem;
  position: relative;
}

.timbratura-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.timbratura-item.dragging {
  opacity: 0.5;
  cursor: grabbing;
  transform: rotate(5deg);
}

.timbratura-item.has-error {
  border-left: 4px solid #FFA726;
  background: #FFF8E1;
}

.timbratura-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.timbratura-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 1.1rem;
}

.timbratura-cliente {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #495057;
  font-size: 0.9rem;
}

.timbratura-location {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
  font-size: 0.85rem;
}

.timbratura-actions {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex-shrink: 0;
  align-items: center;
  position: relative;
  z-index: 2;
}

.edit-btn {
  color: #17a2b8 !important;
  font-size: 0.9rem;
  padding: 0.25rem;
  min-width: 32px;
  height: 32px;
}

.edit-btn:hover {
  color: #138496 !important;
  background: #d1ecf1 !important;
}

.delete-btn {
  color: #dc3545 !important;
  font-size: 0.9rem;
  padding: 0.25rem;
  min-width: 32px;
  height: 32px;
}

.delete-btn:hover {
  color: #c82333 !important;
  background: #f8d7da !important;
}

.empty-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  border: 2px dashed #dee2e6;
  border-radius: 6px;
  transition: all 0.2s ease;
  background: #fafafa;
}

.empty-cell:hover {
  border-color: #28a745;
  background: #f1f9f3;
}

.add-btn {
  color: #28a745 !important;
  font-size: 1.5rem;
  padding: 0.75rem;
  min-width: 48px;
  height: 48px;
}

.add-btn:hover {
  color: #218838 !important;
  background: #d4edda !important;
  transform: scale(1.1);
}

.actions-cell {
  text-align: center;
  vertical-align: middle;
  padding: 1rem 0.5rem;
}

.actions-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  min-height: 80px;
  justify-content: center;
}

.inserimento-timbratura-content {
  padding: 1rem 0;
}

.p-inputtext {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  transition: border-color 0.2s;
}

.p-inputtext:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Stili per il dialog di modifica singola */
.modifica-singola-content {
  padding: 1rem 0;
}

.modifica-singola-content .p-inputtext {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  transition: border-color 0.2s;
}

.modifica-singola-content .p-inputtext:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Stili per il dialog di conferma eliminazione */
.conferma-eliminazione-content {
  padding: 1rem 0;
}

.conferma-eliminazione-content .text-yellow-500 {
  color: #fbbf24;
}

.conferma-eliminazione-content .text-gray-600 {
  color: #6b7280;
}

.conferma-eliminazione-content .text-2xl {
  font-size: 1.5rem;
}

/* Miglioramenti per gli errori nelle timbrature */
.timbratura-errore {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #c53030;
}

/* Hover effects per i pulsanti */
.p-button-rounded:hover {
  transform: scale(1.05);
  transition: transform 0.2s ease;
}

/* Loading states */
.p-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Focus states per accessibility */
.p-button:focus,
.p-inputtext:focus,
.p-dropdown:focus {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

/* Stili per righe completamente eliminate */
.timbratura-row.riga-eliminata {
  background-color: #f8f9fa;
  opacity: 0.6;
}

.timbratura-row.riga-eliminata * {
  color: #6c757d !important;
}

/* Stili per righe parzialmente eliminate */
.timbratura-row.riga-parzialmente-eliminata {
  background-color: #fff3e0;
  border-left: 3px solid #ff9800;
}

/* Cella eliminata completamente */
.timbratura-cell.eliminata {
  background-color: #f1f3f4;
  border-color: #dadce0;
}

.timbratura-item.eliminata {
  background-color: #f8f9fa;
  border-color: #dadce0;
  cursor: not-allowed;
  opacity: 0.7;
}

.timbratura-item.eliminata:hover {
  transform: none;
  box-shadow: none;
}

/* Stili per celle con timbrature eliminate singolarmente */
.timbratura-cell.timbratura-eliminata {
  background-color: #f5f5f5;
  border-color: #bdbdbd;
  opacity: 0.7;
  position: relative;
}

.timbratura-cell.timbratura-eliminata::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  pointer-events: none;
  z-index: 1;
}

/* Stili per timbrature eliminate singolarmente */
.timbratura-item.timbratura-eliminata {
  background-color: #fafafa;
  border-color: #bdbdbd;
  cursor: not-allowed;
  opacity: 0.6;
  border-left: 4px solid #757575;
}

.timbratura-item.timbratura-eliminata:hover {
  transform: none;
  box-shadow: none;
  background-color: #fafafa;
}

.timbratura-item.timbratura-eliminata .timbratura-info * {
  color: #757575 !important;
}

/* Hover effects disabilitati per timbrature eliminate */
.timbratura-cell.timbratura-eliminata:hover {
  background-color: #f5f5f5;
}

/* Numero riga eliminata */
.row-number.eliminata {
  background-color: #e9ecef;
  color: #6c757d;
  position: relative;
}

.eliminata-label {
  display: block;
  font-size: 0.7rem;
  color: #dc3545;
  font-weight: 600;
  margin-top: 0.25rem;
  text-transform: uppercase;
}

/* Placeholder per celle eliminate */
.eliminata-placeholder {
  color: #6c757d;
  font-style: italic;
  font-size: 0.9rem;
  text-align: center;
  padding: 1rem;
}

.empty-cell.eliminata {
  background-color: #f8f9fa;
  border-color: #dadce0;
  cursor: not-allowed;
}

/* Badge di stato per timbrature */
.status-badge {
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
  border-radius: 12px;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 0.5rem;
  transition: all 0.2s ease;
}

.status-badge.nuova {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-badge.modificata {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.status-badge.eliminata {
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
}

/* Timbrature modificate */
.timbratura-item.modificata {
  border-left: 4px solid #ffc107;
  background-color: #fff8e1;
}

.timbratura-item.nuova {
  border-left: 4px solid #28a745;
  background-color: #f1f9f3;
}

.timbratura-item.stato-modificato {
  border-left: 4px solid #2196f3;
  background-color: #e3f2fd;
}

/* Colonna azioni per righe eliminate */
.actions-cell.eliminata {
  background-color: #f8f9fa;
}

/* Indicatori modifiche migliorati */
.modified-indicator {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #856404;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  white-space: nowrap;
  margin-top: 0.5rem;
}

.modified-indicator i {
  font-size: 0.8rem;
}

/* Indicatori per diversi tipi di modifica */
.modified-indicator:has-text("Eliminata") {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.modified-indicator:has-text("Parziale") {
  background: #fff3e0;
  border: 1px solid #ffcc02;
  color: #e65100;
}

.modified-indicator:has-text("Timbrature eliminate") {
  background: #ffebee;
  border: 1px solid #ffcdd2;
  color: #c62828;
}

.modified-indicator:has-text("Spostata") {
  background: #d1ecf1;
  border: 1px solid #bee5eb;
  color: #0c5460;
}

/* Pulsante ripristina */
.p-button-success.p-button-text {
  color: #28a745 !important;
}

.p-button-success.p-button-text:hover {
  color: #218838 !important;
  background: #d4edda !important;
}

.restore-btn {
  color: #4caf50 !important;
  background-color: transparent !important;
}

.restore-btn:hover {
  color: #388e3c !important;
  background: #e8f5e8 !important;
}

/* Transizioni per migliorare l'UX */
.timbratura-row {
  transition: all 0.3s ease;
}

.timbratura-item {
  transition: all 0.3s ease;
}

/* Animazioni per stati di eliminazione */
.timbratura-item.timbratura-eliminata {
  transition: all 0.3s ease-in-out;
}

.timbratura-cell.timbratura-eliminata {
  transition: all 0.3s ease-in-out;
}

/* Disabilita drag per timbrature eliminate */
.timbratura-item.timbratura-eliminata[draggable="false"] {
  cursor: not-allowed;
}

/* Evidenzia le righe spostate */
.timbratura-row:has(.modified-indicator:contains("Spostata")) {
  background: linear-gradient(90deg, #e3f2fd 0%, transparent 100%);
  border-left: 3px solid #2196F3;
}

/* Effetti hover disabilitati per righe eliminate */
.timbratura-row.riga-eliminata:hover {
  background-color: #f8f9fa;
}

.timbratura-row.riga-eliminata .timbratura-cell:hover {
  background-color: #f1f3f4;
}

/* Dark mode support (se necessario) */
@media (prefers-color-scheme: dark) {
  .timbratura-item {
    background: #2d3748;
    border-color: #4a5568;
    color: #e2e8f0;
  }

  .empty-cell {
    background: #1a202c;
    border-color: #4a5568;
  }

  .modified-indicator {
    background: #2d3748;
    border-color: #4a5568;
    color: #fbb6ce;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .timing-header .flex {
    flex-direction: column;
    align-items: stretch;
  }

  .date-navigator {
    justify-content: center;
  }

  .periodo-display {
    min-width: auto;
  }

  .interval-display {
    min-width: 200px;
  }

  .date-picker-wrapper {
    left: -50px;
    right: -50px;
    width: auto;
  }

  .timbrature-grid {
    min-width: 600px;
  }

  .timbratura-actions {
    flex-direction: row;
    gap: 0.5rem;
  }

  .actions-column {
    min-height: 60px;
  }

  .modified-indicator {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }

  .edit-btn,
  .delete-btn,
  .restore-btn {
    min-width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }

  .add-btn {
    min-width: 40px;
    height: 40px;
    font-size: 1.3rem;
  }

  .timbratura-time {
    font-size: 1rem;
  }

  .timbratura-cliente,
  .timbratura-location {
    font-size: 0.8rem;
  }

  .status-badge {
    font-size: 0.6rem;
    padding: 0.15rem 0.3rem;
    margin-left: 0.25rem;
  }

  .eliminata-label {
    font-size: 0.6rem;
  }

  .eliminata-placeholder {
    font-size: 0.8rem;
    padding: 0.75rem;
  }
}
</style>